// src/access/examEntitlements.ts
import { getExamLanguageContext, getExamTypesForLanguage } from "@examflow/core";
function normalizeExamType(examType) {
  return String(examType ?? "").trim().toLowerCase();
}
function normalizeLanguageId(languageId) {
  return String(languageId ?? "").trim().toLowerCase();
}
function isGrantActive(grant, now = /* @__PURE__ */ new Date()) {
  return !grant.expiresAt || new Date(grant.expiresAt) > now;
}
function hasActiveEntitlement(entitlements, now = /* @__PURE__ */ new Date()) {
  return (entitlements?.grants ?? []).some((grant) => isGrantActive(grant, now));
}
function grantMatchesExam(grant, examType) {
  const hasPlatformScope = (grant.platformScopes ?? []).map((scope) => normalizeExamType(scope.examType)).includes(examType);
  const hasExam = grant.examTypes.map(normalizeExamType).includes(examType);
  const examLanguageId = getExamLanguageContext(examType)?.languageId;
  const hasLanguage = Boolean(
    examLanguageId && (grant.languageIds ?? []).map(normalizeLanguageId).includes(examLanguageId)
  );
  return hasPlatformScope || hasExam || hasLanguage;
}
function canAccessExam(entitlements, examType, now = /* @__PURE__ */ new Date()) {
  const target = normalizeExamType(examType);
  if (!target || !entitlements) return false;
  return entitlements.grants.some((grant) => {
    return grantMatchesExam(grant, target) && isGrantActive(grant, now);
  });
}
function resolveExamEntitlementStatus(entitlements, examType, now = /* @__PURE__ */ new Date()) {
  const target = normalizeExamType(examType);
  if (!target || !entitlements) return "none";
  const matching = entitlements.grants.filter((grant) => grantMatchesExam(grant, target));
  if (matching.length === 0) return "none";
  if (matching.some((grant) => isGrantActive(grant, now))) return "active";
  return "expired";
}
function canAccessLanguage(entitlements, languageId, now = /* @__PURE__ */ new Date()) {
  const target = normalizeLanguageId(languageId);
  if (!target || !entitlements) return false;
  return entitlements.grants.some((grant) => {
    const hasPlatformLanguage = (grant.platformScopes ?? []).some(
      (scope) => normalizeLanguageId(scope.languageId) === target
    );
    const hasLanguage = (grant.languageIds ?? []).map(normalizeLanguageId).includes(target);
    const hasExamInLanguage = grant.examTypes.map(normalizeExamType).some((examType) => getExamLanguageContext(examType)?.languageId === target);
    return (hasPlatformLanguage || hasLanguage || hasExamInLanguage) && isGrantActive(grant, now);
  });
}
function chooseDefaultActiveExam(entitlements, now = /* @__PURE__ */ new Date(), currentExam) {
  const current = normalizeExamType(currentExam);
  if (current && canAccessExam(entitlements, current, now)) return current;
  const validGrants = (entitlements?.grants ?? []).filter((grant) => isGrantActive(grant, now)).sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime());
  for (const grant of validGrants) {
    for (const scope of grant.platformScopes ?? []) {
      const examType = normalizeExamType(scope.examType);
      if (examType) return examType;
    }
    for (const value of grant.examTypes) {
      const examType = normalizeExamType(value);
      if (examType) return examType;
    }
    for (const value of grant.languageIds ?? []) {
      const languageId = normalizeLanguageId(value);
      const firstExamType = getExamTypesForLanguage(languageId)[0];
      if (firstExamType) return firstExamType;
    }
  }
  return null;
}
function getEntitlementExpiry(entitlements, examType, now = /* @__PURE__ */ new Date()) {
  const target = normalizeExamType(examType);
  if (!target || !entitlements) return null;
  const matchingGrants = entitlements.grants.filter((grant) => {
    return grantMatchesExam(grant, target) && isGrantActive(grant, now);
  }).sort((a, b) => {
    if (!a.expiresAt) return -1;
    if (!b.expiresAt) return 1;
    return new Date(b.expiresAt).getTime() - new Date(a.expiresAt).getTime();
  });
  return matchingGrants[0]?.expiresAt ?? null;
}

// src/access/learnerBankAccess.ts
import { getExamLanguageContext as getExamLanguageContext2 } from "@examflow/core";
import { getExamConfig } from "@examflow/exams";
function normalize(value) {
  return String(value || "").trim().toLowerCase();
}
function formatLearnerSectionLabel(examType, sectionId) {
  const name = getExamConfig(examType)?.sections.find((section) => section.id === sectionId)?.name;
  if (!name) return sectionId;
  return name.replace(/\s*\([^)]*\)\s*$/u, "").trim() || name;
}
function isLearnerAccessibleLiveBank(bank) {
  return bank.access?.hasAccess === true && bank.isPublished !== false && (bank.questionCount ?? 0) > 0;
}
function buildLearnerAccessibleBankIdSet(banks) {
  return new Set(banks.filter(isLearnerAccessibleLiveBank).map((bank) => bank.id));
}
function normalizeSectionParam(value) {
  const normalized = normalize(value);
  return normalized && normalized !== "all" && normalized !== "result" && normalized !== "diagnosis" ? normalized : null;
}
function resolveLearnerTrainingSectionId({
  sectionId,
  reviewFocus,
  trainingModule
}) {
  return normalizeSectionParam(sectionId) ?? normalizeSectionParam(reviewFocus) ?? normalizeSectionParam(trainingModule);
}
function selectLearnerTrainingBankCandidates({
  banks,
  examType,
  sectionId
}) {
  const targetExamType = normalize(examType);
  const targetLanguageId = getExamLanguageContext2(targetExamType)?.languageId ?? null;
  const targetSectionId = normalizeSectionParam(sectionId);
  return banks.filter((bank) => {
    if (!isLearnerAccessibleLiveBank(bank)) return false;
    if (normalize(bank.examType) !== targetExamType) return false;
    if (targetLanguageId && bank.languageId && normalize(bank.languageId) !== targetLanguageId) {
      return false;
    }
    const bankSection = normalize(bank.section);
    if (targetSectionId && bankSection && bankSection !== targetSectionId) return false;
    return true;
  });
}
function buildLearnerContentAccessGate({
  accessibleBanks,
  examType,
  sectionId,
  accessStatus = "ready"
}) {
  if (accessStatus === "unavailable") {
    return {
      status: "accessUnavailable",
      candidates: [],
      message: "\u6682\u65F6\u65E0\u6CD5\u786E\u8BA4\u53EF\u5B66\u5185\u5BB9\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002"
    };
  }
  const candidates = selectLearnerTrainingBankCandidates({
    banks: accessibleBanks,
    examType,
    sectionId
  });
  if (candidates.length > 0) {
    return {
      status: "ready",
      candidates,
      message: null
    };
  }
  const sectionLabel = sectionId ? formatLearnerSectionLabel(examType, sectionId) : "\u5F53\u524D\u8303\u56F4";
  return {
    status: "contentUnavailable",
    candidates,
    message: `\u5F53\u524D\u8003\u8BD5\u7A7A\u95F4\u5DF2\u6388\u6743\uFF0C\u4F46${sectionLabel}\u6682\u65E0\u5DF2\u53D1\u5E03\u4E14\u53EF\u5B66\u4E60\u7684\u9898\u5E93\u3002`
  };
}

// src/routing/learningRouteScope.ts
import { buildPlatformScope } from "@examflow/core";
function buildLearningRouteScope(searchParams) {
  const scope = buildPlatformScope({
    languageId: searchParams.get("languageId"),
    examType: searchParams.get("examType")
  });
  if (!scope) {
    return {
      languageId: null,
      examType: null
    };
  }
  return {
    languageId: scope.languageId,
    examType: scope.examType
  };
}
function canEnterLearningRoute(search, hasLanguagePreference, hasExamPreference) {
  if (hasLanguagePreference && hasExamPreference) {
    return true;
  }
  const routeScope = buildLearningRouteScope(new URLSearchParams(search));
  return Boolean(routeScope.languageId && routeScope.examType);
}
function buildScopedLearningHref(path, scope, params = {}) {
  const query = new URLSearchParams();
  if (scope?.languageId) query.set("languageId", scope.languageId);
  if (scope?.examType) query.set("examType", scope.examType);
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== void 0 && value !== "") {
      query.set(key, String(value));
    }
  }
  const serialized = query.toString();
  return serialized ? `${path}?${serialized}` : path;
}

// src/routing/trainingLaunch.ts
import { buildPlatformScope as buildPlatformScope2 } from "@examflow/core";
import {
  getExamConfig as getExamConfig2,
  isLevelSupportedForExam,
  normalizeOptionalContentTopicId,
  normalizeOptionalWritingTaskNumber
} from "@examflow/exams";
var TRAINING_INTENSITY_PRESETS = {
  short: { questionCount: 6, durationMinutes: 12 },
  standard: { questionCount: 12, durationMinutes: 24 },
  full: { questionCount: 18, durationMinutes: 36 }
};
var OPEN_RESPONSE_SESSION_PRESET = {
  questionCount: 1,
  durationMinutes: 20
};
function isOpenResponseSkillId(skillId) {
  return skillId === "writing" || skillId === "speaking" || skillId.endsWith("_speaking");
}
function isOpenResponseRuntimeReadySkillId(skillId) {
  return skillId === "writing";
}
function resolveOpenResponseRuntimeBlockReason(skillId) {
  if (!isOpenResponseSkillId(skillId)) return null;
  if (isOpenResponseRuntimeReadySkillId(skillId)) return null;
  return "\u53E3\u8BED\u5F00\u653E\u4F5C\u7B54\u5C1A\u672A\u63A5\u5165\uFF0C\u8BF7\u5148\u7EC3\u5199\u4F5C\u6216\u5176\u4ED6\u9009\u62E9\u9898\u79D1\u76EE\u3002";
}
function resolveSessionSizeForSkill(skillId, intensity) {
  if (isOpenResponseSkillId(skillId)) {
    return { ...OPEN_RESPONSE_SESSION_PRESET };
  }
  return TRAINING_INTENSITY_PRESETS[intensity];
}
var DIAGNOSIS_MODULE_ID = "diagnosis";
function isLegacySessionInput(input) {
  return "contentPack" in input && "moduleId" in input;
}
function resolveTrainingIntensityPreset(intensity) {
  return TRAINING_INTENSITY_PRESETS[intensity];
}
function buildTrainingEntryHref(intent) {
  const scope = {
    languageId: intent.languageId,
    examType: intent.examType
  };
  return buildScopedLearningHref("/training", scope, {
    moduleId: intent.skillId,
    intensity: intent.intensityHint,
    taskNumber: intent.skillId === "writing" && intent.taskNumber != null ? intent.taskNumber : void 0
  });
}
function parseTrainingLaunchIntent(search) {
  const params = typeof search === "string" ? new URLSearchParams(search.replace(/^\?/, "")) : search;
  const languageId = params.get("languageId")?.trim() || buildPlatformScope2({ examType: params.get("examType") })?.languageId || null;
  const examTypeRaw = params.get("examType")?.trim().toLowerCase() || params.get("trainingPack")?.trim().toLowerCase() || params.get("pack")?.trim().toLowerCase() || null;
  const skillId = params.get("moduleId")?.trim().toLowerCase() || params.get("skillId")?.trim().toLowerCase() || params.get("trainingModule")?.trim().toLowerCase() || params.get("module")?.trim().toLowerCase() || params.get("sectionId")?.trim().toLowerCase() || null;
  const intensityHint = parseIntensity(params.get("intensity") ?? params.get("trainingIntensity"));
  const taskNumber = normalizeOptionalSessionTaskNumber(
    examTypeRaw,
    skillId,
    params.get("taskNumber")
  );
  if (!languageId || !examTypeRaw || !skillId) return null;
  if (!buildPlatformScope2({ examType: examTypeRaw })) return null;
  return {
    languageId,
    examType: examTypeRaw,
    skillId,
    intensityHint: intensityHint ?? void 0,
    taskNumber
  };
}
function buildTrainingSessionHref(input) {
  const spec = normalizeToSessionSpec(input);
  if (!spec) {
    throw new Error("Invalid training session launch input");
  }
  const params = new URLSearchParams({
    mode: "practice",
    languageId: spec.languageId,
    examType: spec.examType,
    skillId: spec.skillId,
    intensity: spec.intensity,
    questionCount: String(spec.questionCount)
  });
  if (spec.skillId !== DIAGNOSIS_MODULE_ID) {
    params.set("sectionId", spec.skillId);
  }
  params.set("trainingPack", spec.examType);
  params.set("trainingModule", spec.skillId);
  params.set("trainingIntensity", spec.intensity);
  if (spec.source) params.set("trainingSource", spec.source);
  if (spec.reviewFocus) params.set("reviewFocus", spec.reviewFocus);
  if (spec.next) params.set("next", spec.next);
  if (spec.level) params.set("level", spec.level);
  if (spec.taskNumber != null) params.set("taskNumber", String(spec.taskNumber));
  if (spec.topicId) params.set("topicId", spec.topicId);
  return `/quiz?${params.toString()}`;
}
function parseTrainingSessionSpec(search) {
  const params = typeof search === "string" ? new URLSearchParams(search.replace(/^\?/, "")) : search;
  const examTypeRaw = params.get("examType")?.trim().toLowerCase() || params.get("trainingPack")?.trim().toLowerCase() || params.get("pack")?.trim().toLowerCase() || null;
  const skillId = params.get("skillId")?.trim().toLowerCase() || params.get("trainingModule")?.trim().toLowerCase() || params.get("module")?.trim().toLowerCase() || params.get("sectionId")?.trim().toLowerCase() || null;
  const intensity = parseIntensity(params.get("intensity") ?? params.get("trainingIntensity"));
  const languageId = params.get("languageId")?.trim() || (examTypeRaw ? buildPlatformScope2({ examType: examTypeRaw })?.languageId : null) || null;
  if (!examTypeRaw || !skillId || !intensity || !languageId) return null;
  if (!buildPlatformScope2({ examType: examTypeRaw })) return null;
  if (!isAllowedSkillId(examTypeRaw, skillId)) return null;
  const preset = resolveSessionSizeForSkill(skillId, intensity);
  const source = parseSource(params.get("trainingSource"));
  const reviewFocus = params.get("reviewFocus")?.trim() || void 0;
  const next = sanitizeNextHref(params.get("next"));
  if (source === "remediation" && !reviewFocus) return null;
  const level = normalizeOptionalLevel(examTypeRaw, params.get("level"));
  const taskNumber = normalizeOptionalSessionTaskNumber(
    examTypeRaw,
    skillId,
    params.get("taskNumber")
  );
  const topicId = normalizeOptionalSessionTopicId(examTypeRaw, skillId, params.get("topicId"));
  return {
    languageId,
    examType: examTypeRaw,
    skillId,
    intensity,
    questionCount: preset.questionCount,
    durationMinutes: preset.durationMinutes,
    level,
    taskNumber,
    topicId,
    source,
    reviewFocus,
    next: next ?? void 0
  };
}
function resolveTrainingSessionConfig(search) {
  const fromSpec = parseTrainingSessionSpec(search);
  if (fromSpec) {
    return {
      contentPack: fromSpec.examType,
      moduleId: fromSpec.skillId,
      intensity: fromSpec.intensity,
      questionCount: fromSpec.questionCount,
      durationMinutes: fromSpec.durationMinutes
    };
  }
  throw new Error(
    "Invalid training session launch: missing PlatformScope / skill / intensity (no TCF fallback)"
  );
}
function normalizeToSessionSpec(input) {
  if ("questionCount" in input && "durationMinutes" in input && "examType" in input) {
    const level = normalizeOptionalLevel(input.examType, input.level);
    const taskNumber = normalizeOptionalSessionTaskNumber(
      input.examType,
      input.skillId,
      input.taskNumber
    );
    const topicId = normalizeOptionalSessionTopicId(input.examType, input.skillId, input.topicId);
    const next = {
      ...input,
      level,
      taskNumber,
      topicId
    };
    return next;
  }
  if (isLegacySessionInput(input)) {
    const scope2 = buildPlatformScope2({
      examType: input.contentPack,
      skillId: input.moduleId
    });
    const languageId2 = input.languageId ?? scope2?.languageId;
    if (!languageId2) return null;
    const preset2 = resolveSessionSizeForSkill(input.moduleId, input.intensity);
    return {
      languageId: languageId2,
      examType: input.contentPack,
      skillId: input.moduleId,
      intensity: input.intensity,
      questionCount: preset2.questionCount,
      durationMinutes: preset2.durationMinutes,
      level: normalizeOptionalLevel(input.contentPack, input.level),
      taskNumber: normalizeOptionalSessionTaskNumber(
        input.contentPack,
        input.moduleId,
        input.taskNumber
      ),
      topicId: normalizeOptionalSessionTopicId(input.contentPack, input.moduleId, input.topicId),
      source: input.source,
      reviewFocus: input.reviewFocus,
      next: input.next
    };
  }
  const scope = buildPlatformScope2({
    examType: input.examType,
    skillId: input.skillId
  });
  const languageId = input.languageId ?? scope?.languageId;
  if (!languageId) return null;
  const preset = resolveSessionSizeForSkill(input.skillId, input.intensity);
  return {
    languageId,
    examType: input.examType,
    skillId: input.skillId,
    intensity: input.intensity,
    questionCount: preset.questionCount,
    durationMinutes: preset.durationMinutes,
    level: normalizeOptionalLevel(input.examType, input.level),
    taskNumber: normalizeOptionalSessionTaskNumber(
      input.examType,
      input.skillId,
      input.taskNumber
    ),
    topicId: normalizeOptionalSessionTopicId(input.examType, input.skillId, input.topicId),
    source: input.source,
    reviewFocus: input.reviewFocus,
    next: input.next
  };
}
function normalizeOptionalLevel(examType, level) {
  const trimmed = level?.trim();
  if (!trimmed) return void 0;
  if (!isLevelSupportedForExam(examType, trimmed)) return void 0;
  return trimmed;
}
function normalizeOptionalSessionTaskNumber(examType, skillId, raw) {
  if (skillId !== "writing") return void 0;
  if (!examType) return void 0;
  return normalizeOptionalWritingTaskNumber(examType, raw);
}
function normalizeOptionalSessionTopicId(examType, skillId, raw) {
  if (skillId !== "writing") return void 0;
  if (!examType) return void 0;
  return normalizeOptionalContentTopicId(examType, "writing", raw);
}
function parseIntensity(value) {
  if (value === "short" || value === "standard" || value === "full") return value;
  return null;
}
function parseSource(value) {
  if (value === "standard" || value === "remediation" || value === "diagnosis") return value;
  return void 0;
}
function isAllowedSkillId(examType, skillId) {
  if (skillId === DIAGNOSIS_MODULE_ID) return true;
  const sections = getExamConfig2(examType)?.sections ?? [];
  if (sections.length === 0) return skillId.length > 0;
  return sections.some((section) => section.id === skillId);
}
function sanitizeNextHref(value) {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

// src/routing/attemptSpec.ts
import { buildPlatformScope as buildPlatformScope3 } from "@examflow/core";
function resolveLaunchScope(params) {
  const examTypeRaw = params.get("examType")?.trim().toLowerCase() || params.get("trainingPack")?.trim().toLowerCase() || params.get("pack")?.trim().toLowerCase() || null;
  if (!examTypeRaw) return null;
  const scope = buildPlatformScope3({
    languageId: params.get("languageId"),
    examType: examTypeRaw
  });
  if (!scope) return null;
  return {
    languageId: scope.languageId,
    examType: scope.examType
  };
}
function isPracticeAttempt(spec) {
  return spec.kind === "training" || spec.kind === "diagnosis";
}
function isMockAttempt(spec) {
  return spec.kind === "mock";
}
function attemptSpecToTrainingSession(spec) {
  return {
    languageId: spec.languageId,
    examType: spec.examType,
    skillId: spec.skillId,
    intensity: spec.intensity,
    questionCount: spec.questionCount,
    durationMinutes: spec.durationMinutes,
    level: spec.level,
    taskNumber: spec.kind === "training" ? spec.taskNumber : void 0,
    topicId: spec.kind === "training" ? spec.topicId : void 0,
    // Omit standard on the wire — absence means standard (matches buildTrainingSessionHref).
    source: spec.source === "standard" ? void 0 : spec.source,
    reviewFocus: spec.kind === "training" ? spec.reviewFocus : void 0,
    next: spec.next
  };
}
function parseAttemptSpec(search) {
  const params = typeof search === "string" ? new URLSearchParams(search.replace(/^\?/, "")) : search;
  if (params.get("mode") === "exam") {
    const scope = resolveLaunchScope(params);
    if (!scope) return null;
    return {
      kind: "mock",
      languageId: scope.languageId,
      examType: scope.examType
    };
  }
  const session = parseTrainingSessionSpec(params);
  if (!session) return null;
  const source = session.source ?? "standard";
  if (source === "diagnosis" || session.skillId === "diagnosis") {
    const questionCountParam = params.get("questionCount");
    const questionCount = questionCountParam ? Math.max(parseInt(questionCountParam, 10) || session.questionCount, 1) : session.questionCount;
    return {
      kind: "diagnosis",
      languageId: session.languageId,
      examType: session.examType,
      skillId: "diagnosis",
      intensity: session.intensity,
      questionCount,
      durationMinutes: session.durationMinutes,
      level: session.level,
      source: "diagnosis",
      next: session.next
    };
  }
  if (source === "remediation") {
    if (!session.reviewFocus) return null;
    return {
      kind: "training",
      languageId: session.languageId,
      examType: session.examType,
      skillId: session.skillId,
      intensity: session.intensity,
      questionCount: session.questionCount,
      durationMinutes: session.durationMinutes,
      level: session.level,
      taskNumber: session.taskNumber,
      topicId: session.topicId,
      source: "remediation",
      reviewFocus: session.reviewFocus,
      next: session.next
    };
  }
  return {
    kind: "training",
    languageId: session.languageId,
    examType: session.examType,
    skillId: session.skillId,
    intensity: session.intensity,
    questionCount: session.questionCount,
    durationMinutes: session.durationMinutes,
    level: session.level,
    taskNumber: session.taskNumber,
    topicId: session.topicId,
    source: "standard",
    reviewFocus: session.reviewFocus,
    next: session.next
  };
}
function normalizeAttemptSpec(input) {
  if (input.kind === "mock" && typeof input.languageId === "string" && input.languageId.length > 0) {
    return {
      kind: "mock",
      languageId: input.languageId,
      examType: input.examType
    };
  }
  if ((input.kind === "training" || input.kind === "diagnosis") && typeof input.languageId === "string" && input.languageId.length > 0 && "durationMinutes" in input && typeof input.durationMinutes === "number") {
    const level = normalizeOptionalLevel(input.examType, input.level);
    const taskNumber = input.kind === "training" ? normalizeOptionalSessionTaskNumber(input.examType, input.skillId, input.taskNumber) : void 0;
    const topicId = input.kind === "training" ? normalizeOptionalSessionTopicId(input.examType, input.skillId, input.topicId) : void 0;
    return {
      ...input,
      level,
      ...input.kind === "training" ? { taskNumber, topicId } : {}
    };
  }
  if (input.kind === "mock") {
    const scope2 = buildPlatformScope3({
      languageId: input.languageId,
      examType: input.examType
    });
    if (!scope2) return null;
    return {
      kind: "mock",
      languageId: scope2.languageId,
      examType: scope2.examType
    };
  }
  if (input.kind === "diagnosis") {
    const scope2 = buildPlatformScope3({
      languageId: input.languageId,
      examType: input.examType
    });
    if (!scope2) return null;
    const intensity = input.intensity ?? "short";
    const preset2 = resolveSessionSizeForSkill("diagnosis", intensity);
    return {
      kind: "diagnosis",
      languageId: scope2.languageId,
      examType: scope2.examType,
      skillId: "diagnosis",
      intensity,
      questionCount: Math.max(input.questionCount, 1),
      durationMinutes: preset2.durationMinutes,
      level: normalizeOptionalLevel(scope2.examType, input.level),
      source: "diagnosis",
      next: input.next
    };
  }
  if (input.kind === "remediation") {
    const scope2 = buildPlatformScope3({
      languageId: input.languageId,
      examType: input.examType
    });
    if (!scope2 || !input.reviewFocus) return null;
    const preset2 = resolveSessionSizeForSkill(input.skillId, input.intensity);
    return {
      kind: "training",
      languageId: scope2.languageId,
      examType: scope2.examType,
      skillId: input.skillId,
      intensity: input.intensity,
      questionCount: preset2.questionCount,
      durationMinutes: preset2.durationMinutes,
      level: normalizeOptionalLevel(scope2.examType, input.level),
      taskNumber: normalizeOptionalSessionTaskNumber(
        scope2.examType,
        input.skillId,
        input.taskNumber
      ),
      topicId: normalizeOptionalSessionTopicId(scope2.examType, input.skillId, input.topicId),
      source: "remediation",
      reviewFocus: input.reviewFocus,
      next: input.next
    };
  }
  const scope = buildPlatformScope3({
    languageId: input.languageId,
    examType: input.examType
  });
  if (!scope) return null;
  const preset = resolveSessionSizeForSkill(input.skillId, input.intensity);
  const source = input.source ?? "standard";
  if (source === "remediation" && !input.reviewFocus) return null;
  return {
    kind: "training",
    languageId: scope.languageId,
    examType: scope.examType,
    skillId: input.skillId,
    intensity: input.intensity,
    questionCount: preset.questionCount,
    durationMinutes: preset.durationMinutes,
    level: normalizeOptionalLevel(scope.examType, input.level),
    taskNumber: normalizeOptionalSessionTaskNumber(
      scope.examType,
      input.skillId,
      input.taskNumber
    ),
    topicId: normalizeOptionalSessionTopicId(scope.examType, input.skillId, input.topicId),
    source,
    reviewFocus: input.reviewFocus,
    next: input.next
  };
}
function buildAttemptHref(input) {
  const spec = normalizeAttemptSpec(input);
  if (!spec) {
    throw new Error("Invalid AttemptSpec launch input");
  }
  if (spec.kind === "mock") {
    return buildScopedLearningHref(
      "/quiz",
      {
        languageId: spec.languageId,
        examType: spec.examType
      },
      { mode: "exam" }
    );
  }
  const href = buildTrainingSessionHref(attemptSpecToTrainingSession(spec));
  if (spec.kind !== "diagnosis") {
    return href;
  }
  const [path, query = ""] = href.split("?");
  const params = new URLSearchParams(query);
  params.set("questionCount", String(spec.questionCount));
  return `${path}?${params.toString()}`;
}
function attemptWorkspaceHref(spec) {
  return buildScopedLearningHref("/workspace", {
    languageId: spec.languageId,
    examType: spec.examType
  });
}

// src/routing/quizLaunchGate.ts
import { buildPlatformScope as buildPlatformScope4 } from "@examflow/core";
function resolveQuizLaunchScope(search) {
  const params = typeof search === "string" ? new URLSearchParams(search.replace(/^\?/, "")) : search;
  const examTypeRaw = params.get("examType")?.trim().toLowerCase() || params.get("trainingPack")?.trim().toLowerCase() || params.get("pack")?.trim().toLowerCase() || null;
  if (!examTypeRaw) return null;
  const scope = buildPlatformScope4({
    languageId: params.get("languageId"),
    examType: examTypeRaw
  });
  if (!scope) return null;
  return {
    languageId: scope.languageId,
    examType: scope.examType
  };
}
function resolveQuizLaunchGate(search) {
  const params = typeof search === "string" ? new URLSearchParams(search.replace(/^\?/, "")) : search;
  const launchScope = resolveQuizLaunchScope(params);
  const workspaceHref = launchScope ? buildScopedLearningHref("/workspace", launchScope) : "/workspace";
  if (!launchScope) {
    return {
      status: "blocked",
      reason: "missing_exam_scope",
      message: "\u672C\u6B21\u7EC3\u4E60\u7F3A\u5C11\u8003\u8BD5\u8303\u56F4\uFF08languageId / examType\uFF09\u3002\u8BF7\u4ECE\u8003\u8BD5\u7A7A\u95F4\u5DE5\u4F5C\u53F0\u6216\u8BAD\u7EC3\u5165\u53E3\u91CD\u65B0\u5F00\u59CB\uFF0C\u4EE5\u786E\u4FDD\u591A\u8BED\u8A00\u8BC1\u636E\u9694\u79BB\u3002",
      workspaceHref
    };
  }
  const attempt = parseAttemptSpec(params);
  if (!attempt) {
    return {
      status: "blocked",
      reason: "practice_not_evidence_ready",
      message: "\u672C\u6B21\u4E13\u9879\u8BAD\u7EC3\u65E0\u6CD5\u5199\u5165\u5B66\u4E60\u8BC1\u636E\uFF08\u7F3A\u5C11\u5B8C\u6574 AttemptSpec\uFF0C\u6216\u590D\u76D8\u7F3A\u5C11 reviewFocus\uFF09\u3002\u8BF7\u4ECE\u5DE5\u4F5C\u53F0\u300C\u4E0B\u4E00\u6B65\u300D\u6216\u4E13\u9879\u8BAD\u7EC3\u5165\u53E3\u542F\u52A8\u3002",
      workspaceHref: attemptWorkspaceHref(launchScope)
    };
  }
  return {
    status: "ready",
    mode: attempt.kind === "mock" ? "exam" : "practice",
    languageId: attempt.languageId,
    examType: attempt.examType,
    evidenceWritable: true,
    attempt
  };
}

// src/theme/examSpaceTheme.ts
import { buildPlatformScope as buildPlatformScope5 } from "@examflow/core";
var DEFAULT_THEME = {
  accent: "#1f4f46",
  accentSoft: "#e4ece8",
  surface: "#f8faf6",
  aside: "#edf3ec",
  border: "#dbe4d8"
};
var EXAM_SPACE_THEMES = {
  tcf: {
    accent: "#d97745",
    accentSoft: "#f3e6dc",
    surface: "#faf7f3",
    aside: "#f3ebe3",
    border: "#e5d8cc"
  },
  ielts: {
    accent: "#1f4f46",
    accentSoft: "#e4ece8",
    surface: "#f5f8f6",
    aside: "#e8f0ec",
    border: "#d3e0d8"
  },
  toefl: {
    accent: "#2f6b5c",
    accentSoft: "#e3efe9",
    surface: "#f4f8f6",
    aside: "#e6f0eb",
    border: "#cfe0d7"
  },
  jlpt: {
    accent: "#8b4d3b",
    accentSoft: "#f0e5e0",
    surface: "#faf6f4",
    aside: "#f2e9e5",
    border: "#e4d4cc"
  },
  topik: {
    accent: "#3d5a80",
    accentSoft: "#e4eaf2",
    surface: "#f5f7fa",
    aside: "#e8eef5",
    border: "#d0dae8"
  },
  dele: {
    accent: "#b4533a",
    accentSoft: "#f3e5e0",
    surface: "#faf6f4",
    aside: "#f3e9e5",
    border: "#e6d4cc"
  },
  siele: {
    accent: "#b4533a",
    accentSoft: "#f3e5e0",
    surface: "#faf6f4",
    aside: "#f3e9e5",
    border: "#e6d4cc"
  }
};
function resolveExamSpaceTheme(examCode) {
  const code = String(examCode ?? "").trim().toLowerCase();
  const palette = EXAM_SPACE_THEMES[code] ?? DEFAULT_THEME;
  return { examCode: code || "exam", ...palette };
}
function formatExamSpaceIdentityLabel(examCode, fallbackExamName) {
  const scope = buildPlatformScope5({ examType: examCode });
  if (scope) {
    return `${scope.languageName} \xB7 ${scope.examName}`;
  }
  return fallbackExamName?.trim() || examCode.toUpperCase();
}
function formatExamSpaceLanguageCue(scope) {
  if (!scope) return "\u8BED\u8A00\u8003\u8BD5";
  const localeShort = scope.locale.split("-")[0]?.toUpperCase() ?? "";
  return localeShort ? `${scope.languageName} \xB7 ${localeShort}` : scope.languageName;
}

// src/diagnosis/diagnosisPackModel.ts
import { getExamConfig as getExamConfig3 } from "@examflow/exams";
import { buildPlatformScope as buildPlatformScope6 } from "@examflow/core";
var DIAGNOSIS_MODULE_ID2 = "diagnosis";
var DIAGNOSIS_MODULE_LABEL = "\u5165\u95E8\u8BCA\u65AD";
var DIAGNOSIS_INTENSITY = "short";
var DIAGNOSIS_INTENSITY_LABEL = "\u5165\u95E8\u8BCA\u65AD \xB7 \u5206\u9879\u62BD\u6837";
var DIAGNOSIS_QUESTIONS_PER_SKILL = 6;
function resolveReadyDiagnosisSectionIds(examCode, accessibleBanks) {
  const sections = (getExamConfig3(examCode)?.sections ?? []).filter(
    (section) => section.includeInFullMock !== false
  );
  return sections.filter(
    (section) => selectLearnerTrainingBankCandidates({
      banks: accessibleBanks,
      examType: examCode,
      sectionId: section.id
    }).length > 0
  ).map((section) => section.id);
}
function resolveDiagnosisPack(examCode, options = {}) {
  const configured = (getExamConfig3(examCode)?.sections ?? []).filter(
    // Optional open-response tracks (e.g. TCF writing) are Training-only — not diagnosis pack Skills.
    (section) => section.includeInFullMock !== false
  );
  const readySet = normalizeReadySectionSet(options.readySectionIds);
  const allSections = configured.length > 0 ? configured.map((section) => ({
    sectionId: section.id,
    label: section.name.replace(/\s*\([^)]*\)\s*$/u, "").trim() || section.name,
    questionCount: DIAGNOSIS_QUESTIONS_PER_SKILL
  })) : [];
  const sections = readySet == null ? allSections : allSections.filter((section) => readySet.has(section.sectionId.toLowerCase()));
  const omittedSectionLabels = readySet == null ? [] : allSections.filter((section) => !readySet.has(section.sectionId.toLowerCase())).map((section) => section.label);
  const totalQuestions = sections.reduce((total, section) => total + section.questionCount, 0);
  return {
    examCode,
    moduleId: DIAGNOSIS_MODULE_ID2,
    moduleLabel: DIAGNOSIS_MODULE_LABEL,
    intensity: DIAGNOSIS_INTENSITY,
    intensityLabel: DIAGNOSIS_INTENSITY_LABEL,
    totalQuestions,
    sections,
    omittedSectionLabels,
    reason: totalQuestions === 0 ? "\u6682\u65F6\u8FD8\u6CA1\u6709\u53EF\u7EC3\u7684\u9898\u76EE\u3002\u8BF7\u7B49\u5F85 Content Inventory \u53D1\u5E03\u540E\u518D\u5F00\u59CB\u5165\u95E8\u8BCA\u65AD\u3002" : omittedSectionLabels.length > 0 ? `\u5165\u95E8\u8BCA\u65AD\u5C06\u8986\u76D6 ${sections.map((section) => section.label).join("\u3001")}\uFF1B${omittedSectionLabels.join("\u3001")} \u8FD8\u5728\u51C6\u5907\u4E2D\u3002` : "\u5165\u95E8\u8BCA\u65AD\u5C06\u6309 Skill \u5747\u8861\u62BD\u6837\uFF0C\u5EFA\u7ACB\u80FD\u529B\u57FA\u7EBF\u3002"
  };
}
function buildDiagnosisSessionHref(examCode, scope, options = {}) {
  const pack = resolveDiagnosisPack(examCode, options);
  const activeScope = scope ?? buildPlatformScope6({ examType: examCode });
  const languageId = activeScope?.languageId;
  if (!languageId) {
    return buildScopedLearningHref("/workspace", activeScope ?? { languageId: null, examType: examCode });
  }
  return buildAttemptHref({
    kind: "diagnosis",
    languageId,
    examType: pack.examCode,
    questionCount: Math.max(pack.totalQuestions, 1),
    intensity: pack.intensity,
    next: buildScopedLearningHref("/diagnosis/ability", activeScope)
  });
}
function buildDiagnosisEntryAction(examCode, scope, options = {}) {
  const pack = resolveDiagnosisPack(examCode, options);
  const contentBlocked = pack.totalQuestions === 0;
  return {
    id: "start-diagnosis",
    label: "\u5F00\u59CB\u80FD\u529B\u8BCA\u65AD",
    kind: "primary",
    href: contentBlocked ? void 0 : buildDiagnosisSessionHref(examCode, scope, options),
    disabled: Boolean(options.disabled) || contentBlocked,
    disabledReason: contentBlocked ? pack.reason : void 0
  };
}
function sampleDiagnosisPackQuestions(questions, pack) {
  if (pack.totalQuestions <= 0) {
    return [];
  }
  const bySection = /* @__PURE__ */ new Map();
  for (const question of questions) {
    const sectionId = question.section?.trim() || "unknown";
    const bucket = bySection.get(sectionId) ?? [];
    bucket.push(question);
    bySection.set(sectionId, bucket);
  }
  for (const bucket of bySection.values()) {
    shuffleInPlace(bucket);
  }
  const picked = [];
  const pickedIds = /* @__PURE__ */ new Set();
  for (const section of pack.sections) {
    const bucket = bySection.get(section.sectionId) ?? [];
    for (const question of bucket.slice(0, section.questionCount)) {
      picked.push(question);
      pickedIds.add(question.id);
    }
  }
  if (picked.length < pack.totalQuestions) {
    const remainder = shuffleInPlace(questions.filter((question) => !pickedIds.has(question.id)));
    for (const question of remainder) {
      if (picked.length >= pack.totalQuestions) break;
      picked.push(question);
      pickedIds.add(question.id);
    }
  }
  return shuffleInPlace(picked.slice(0, pack.totalQuestions));
}
function normalizeReadySectionSet(readySectionIds) {
  if (readySectionIds == null) {
    return null;
  }
  return new Set(
    readySectionIds.map((sectionId) => sectionId.trim().toLowerCase()).filter((sectionId) => sectionId.length > 0)
  );
}
function shuffleInPlace(items) {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    const current = items[index];
    items[index] = items[swapIndex];
    items[swapIndex] = current;
  }
  return items;
}

// src/recommendations/learningAdvisorModel.ts
import { buildPlatformScope as buildPlatformScope9 } from "@examflow/core";

// src/models/closedChoiceMistakes.ts
function isClosedChoiceWrongMetric(metric) {
  if (metric.responseMode === "open_ended") return false;
  return metric.isCorrect === false;
}
function countClosedChoiceWrongMetrics(metrics) {
  if (!metrics?.length) return 0;
  return metrics.filter(isClosedChoiceWrongMetric).length;
}

// src/models/shared.ts
import {
  buildPlatformScope as buildPlatformScope8,
  getAllAvailableExams,
  getExamLanguageContext as getExamLanguageContext3,
  getSkillSectionLabel
} from "@examflow/core";
import { getExamConfig as getExamConfig5 } from "@examflow/exams";

// src/models/writingEvidence.ts
import { buildPlatformScope as buildPlatformScope7 } from "@examflow/core";
import {
  getExamConfig as getExamConfig4,
  getWritingTaskOptionsForExam,
  normalizeWritingPerformancePercent
} from "@examflow/exams";
var WRITING_EVIDENCE_STALE_MS = 7 * 24 * 60 * 60 * 1e3;
var WRITING_LOW_SCORE_RATIO = 0.55;
var WRITING_STRONG_SCORE_RATIO = 0.75;
function isWritingScopedRecord(record) {
  const skill = String(record.skillId ?? record.moduleId ?? "").trim().toLowerCase();
  return skill === "writing";
}
function pickOpenEndedWritingMetrics(record) {
  return (record.questionMetrics ?? []).filter((metric) => {
    if (typeof metric.aiScore === "number" && Number.isFinite(metric.aiScore)) return true;
    return metric.responseMode === "open_ended";
  });
}
function resolveWeakestDimension(metric) {
  const dims = metric.aiDimensions;
  if (!Array.isArray(dims) || dims.length === 0) return null;
  const ranked = [...dims].filter((dim) => typeof dim.score === "number" && Number.isFinite(dim.score)).sort((a, b) => a.score - b.score || a.id.localeCompare(b.id));
  const weakest = ranked[0];
  if (!weakest) return null;
  const action = typeof weakest.action === "string" ? weakest.action.trim() : "";
  return {
    id: String(weakest.id ?? ""),
    label: String(weakest.label ?? weakest.id ?? "\u5F31\u9879"),
    score: weakest.score,
    action
  };
}
function toSample(record, metric) {
  if (metric.aiGradeSource === "heuristic") return null;
  if (typeof metric.aiScore !== "number" || !Number.isFinite(metric.aiScore)) {
    return null;
  }
  const scoreMax = typeof metric.aiScoreMax === "number" && metric.aiScoreMax > 0 ? metric.aiScoreMax : 20;
  const scoreRatio = Math.max(0, Math.min(1, metric.aiScore / scoreMax));
  const taskRaw = Number(metric.taskNumber);
  const taskNumber = Number.isFinite(taskRaw) && taskRaw > 0 ? Math.trunc(taskRaw) : null;
  return {
    completedAt: record.completedAt,
    taskNumber,
    score: metric.aiScore,
    scoreMax,
    scoreRatio,
    levelHint: typeof metric.aiLevelHint === "string" && metric.aiLevelHint.trim() ? metric.aiLevelHint.trim() : null,
    weakestDimension: resolveWeakestDimension(metric)
  };
}
function taskLabel(examCode, taskNumber) {
  if (taskNumber == null) return "\u5199\u4F5C\u4EFB\u52A1";
  const option = getWritingTaskOptionsForExam(examCode).find(
    (item) => item.value === taskNumber
  );
  return option?.label ?? `\u4EFB\u52A1 ${taskNumber}`;
}
function formatScore(sample) {
  const rounded = sample.scoreMax <= 9 ? Math.round(sample.score * 10) / 10 : Math.round(sample.score);
  return `${rounded}/${sample.scoreMax}`;
}
function averageRatioByTask(samples) {
  const map = /* @__PURE__ */ new Map();
  for (const sample of samples) {
    if (sample.taskNumber == null) continue;
    const existing = map.get(sample.taskNumber) ?? { sum: 0, count: 0 };
    existing.sum += sample.scoreRatio;
    existing.count += 1;
    map.set(sample.taskNumber, existing);
  }
  return map;
}
function resolveRecommendedWritingTaskNumber(examCode, samples) {
  if (samples.length === 0) return null;
  const options = getWritingTaskOptionsForExam(examCode);
  if (options.length === 0) {
    return samples[0]?.taskNumber ?? null;
  }
  const attempted = new Set(
    samples.map((sample) => sample.taskNumber).filter((task) => task != null && task > 0)
  );
  const unattempted = options.find((option) => !attempted.has(option.value));
  if (unattempted) return unattempted.value;
  const byTask = averageRatioByTask(samples);
  let weakestTask = null;
  let weakestAvg = Number.POSITIVE_INFINITY;
  for (const [task, stats] of byTask) {
    const avg = stats.sum / Math.max(1, stats.count);
    if (avg < weakestAvg) {
      weakestAvg = avg;
      weakestTask = task;
    }
  }
  return weakestTask ?? samples[0]?.taskNumber ?? null;
}
function buildFocusReason(examCode, summary, nowMs) {
  const latest = summary.latest;
  if (!latest) {
    return "\u5199\u4F5C\u4E0D\u8FDB\u5168\u771F\u5FC5\u8003\u5377\uFF0C\u4F46\u5EFA\u8BAE\u6BCF\u5468\u81F3\u5C11\u5B8C\u6210\u4E00\u7BC7\u300C\u5199\u2192\u8BC4\u2192\u6539\u300D\u3002";
  }
  const recommendedLabel = taskLabel(
    examCode,
    summary.recommendedTaskNumber ?? latest.taskNumber
  );
  const daysSince = Math.floor(
    Math.max(0, nowMs - latest.completedAt) / (24 * 60 * 60 * 1e3)
  );
  const dimCue = latest.weakestDimension?.action || (latest.weakestDimension ? `\u4F18\u5148\u8865\u300C${latest.weakestDimension.label}\u300D` : null);
  if (nowMs - latest.completedAt >= WRITING_EVIDENCE_STALE_MS) {
    return `\u5DF2 ${Math.max(daysSince, 7)} \u5929\u672A\u5199\u4F5C\uFF08\u6700\u8FD1 ${formatScore(latest)}\uFF09\u3002\u5EFA\u8BAE\u518D\u5199\u4E00\u7BC7${recommendedLabel}\u3002`;
  }
  if (latest.scoreRatio < WRITING_LOW_SCORE_RATIO) {
    return dimCue ? `\u6700\u8FD1 ${taskLabel(examCode, latest.taskNumber)} ${formatScore(latest)} \u504F\u4F4E\u3002${dimCue}\uFF0C\u518D\u5199\u4E00\u7248\u3002` : `\u6700\u8FD1 ${taskLabel(examCode, latest.taskNumber)} ${formatScore(latest)} \u504F\u4F4E\u3002\u5EFA\u8BAE\u540C\u4EFB\u52A1\u518D\u5199\u4E00\u7BC7\u300C\u5199\u2192\u8BC4\u2192\u6539\u300D\u3002`;
  }
  if (latest.scoreRatio >= WRITING_STRONG_SCORE_RATIO && summary.recommendedTaskNumber != null && summary.recommendedTaskNumber !== latest.taskNumber) {
    return `\u6700\u8FD1 ${taskLabel(examCode, latest.taskNumber)} ${formatScore(latest)} \u5DF2\u7A33\u4F4F\u3002\u6362\u7EC3 ${recommendedLabel} \u8986\u76D6\u672A\u7EC3\u4EFB\u52A1\u3002`;
  }
  if (summary.averageScoreRatio != null && summary.averageScoreRatio < WRITING_LOW_SCORE_RATIO) {
    return `\u5199\u4F5C\u5747\u5206\u4ECD\u504F\u4F4E\uFF08\u6700\u8FD1 ${formatScore(latest)}\uFF09\u3002\u7EE7\u7EED ${recommendedLabel}\u300C\u5199\u2192\u8BC4\u2192\u6539\u300D\u3002`;
  }
  return `\u6700\u8FD1 ${taskLabel(examCode, latest.taskNumber)} ${formatScore(latest)}${latest.levelHint ? ` \xB7 ${latest.levelHint}` : ""}\u3002\u5EFA\u8BAE\u518D\u5199\u4E00\u7BC7\u5DE9\u56FA${summary.recommendedTaskNumber != null && summary.recommendedTaskNumber !== latest.taskNumber ? `\uFF08\u4E0B\u4E00\u6279\uFF1A${recommendedLabel}\uFF09` : ""}\u3002`;
}
function buildReportLine(examCode, samples, latest, averageScoreRatio) {
  if (!latest || samples.length === 0) return null;
  const avgPct = averageScoreRatio != null ? normalizeWritingPerformancePercent(averageScoreRatio * 100, 100) : null;
  const dim = latest.weakestDimension ? `\uFF1B\u5F31\u9879 ${latest.weakestDimension.label}` : "";
  return `\u5199\u4F5C\u8BC1\u636E ${samples.length} \u7BC7 \xB7 \u6700\u8FD1 ${taskLabel(examCode, latest.taskNumber)} ${formatScore(latest)}${avgPct != null ? ` \xB7 \u5747\u5206\u7EA6 ${avgPct}%` : ""}${dim}`;
}
function summarizeWritingEvidence(records, examCode, nowMs = Date.now()) {
  const samples = [];
  for (const record of records) {
    if (!isWritingScopedRecord(record)) continue;
    for (const metric of pickOpenEndedWritingMetrics(record)) {
      const sample = toSample(record, metric);
      if (sample) samples.push(sample);
    }
  }
  samples.sort((a, b) => b.completedAt - a.completedAt);
  const latest = samples[0] ?? null;
  const averageScoreRatio = samples.length > 0 ? samples.reduce((sum, sample) => sum + sample.scoreRatio, 0) / samples.length : null;
  const recommendedTaskNumber = resolveRecommendedWritingTaskNumber(examCode, samples);
  const partial = {
    sampleCount: samples.length,
    latest,
    averageScoreRatio,
    recommendedTaskNumber
  };
  return {
    ...partial,
    reportLine: buildReportLine(examCode, samples, latest, averageScoreRatio),
    focusReason: buildFocusReason(examCode, partial, nowMs)
  };
}
function examHasWritingSkill(examCode) {
  return (getExamConfig4(examCode)?.sections ?? []).some((section) => section.id === "writing");
}
function buildWritingTrainingEntryHref(examCode, taskNumber) {
  const languageId = buildPlatformScope7({ examType: examCode })?.languageId ?? "french";
  return buildScopedLearningHref(
    "/training",
    { languageId, examType: examCode },
    {
      moduleId: "writing",
      intensity: "standard",
      taskNumber: taskNumber != null && taskNumber > 0 ? taskNumber : void 0
    }
  );
}
function buildAbilityWritingEvidenceBand(examCode, trainingRecords, nowMs = Date.now()) {
  if (!examHasWritingSkill(examCode)) return null;
  const summary = summarizeWritingEvidence(trainingRecords, examCode, nowMs);
  return {
    summary,
    entryHref: buildWritingTrainingEntryHref(examCode, summary.recommendedTaskNumber),
    actionLabel: summary.sampleCount > 0 ? "\u6309\u5199\u4F5C\u8BC1\u636E\u5F00\u7EC3" : "\u5199\u4E00\u7BC7\uFF08AI \u6279\u6539\uFF09"
  };
}

// src/models/shared.ts
var PASSED_MOCK_SCORE_THRESHOLD = 60;
function buildMockExamEvidenceRecordsFromAbilitySnapshots(snapshots, options) {
  const limit = Math.max(1, options?.limit ?? 20);
  return snapshots.filter((snapshot) => snapshot.source === "mock").slice().sort((a, b) => toSnapshotTime(b.createdAt) - toSnapshotTime(a.createdAt)).slice(0, limit).map((snapshot) => {
    const dimensions = (snapshot.dimensions ?? []).filter((dimension) => dimension.sectionId).map((dimension) => ({
      sectionId: dimension.sectionId,
      answeredCount: Math.max(0, dimension.answeredCount),
      correctCount: Math.max(0, Math.min(dimension.correctCount, dimension.answeredCount))
    }));
    const totalQuestions = dimensions.reduce((sum, dimension) => sum + dimension.answeredCount, 0);
    const correctCount = dimensions.reduce((sum, dimension) => sum + dimension.correctCount, 0);
    const score = typeof snapshot.overallScore === "number" && Number.isFinite(snapshot.overallScore) ? snapshot.overallScore : totalQuestions > 0 ? Math.round(correctCount / totalQuestions * 100) : 0;
    const platformScope = buildPlatformScope8({
      examType: snapshot.examType,
      languageId: snapshot.languageId ?? void 0,
      skillId: snapshot.skillId ?? void 0
    });
    return {
      id: snapshot.id,
      examType: snapshot.examType,
      languageId: snapshot.languageId,
      locale: platformScope?.locale ?? null,
      ttsLocale: platformScope?.ttsLocale ?? null,
      platformScope,
      totalQuestions,
      correctCount,
      score,
      totalScore: 100,
      passed: score >= PASSED_MOCK_SCORE_THRESHOLD,
      duration: 0,
      completedAt: toSnapshotTime(snapshot.createdAt),
      dimensions
    };
  });
}
var TCF_CONTENT_PACK = {
  id: "tcf",
  role: "currentContentPack",
  label: "TCF \u5185\u5BB9\u5305",
  description: "\u5F53\u524D\u7528\u4E8E\u9A8C\u8BC1 ExamFlow \u5E73\u53F0\u8BAD\u7EC3\u95ED\u73AF\u7684\u9996\u4E2A\u8003\u8BD5\u5185\u5BB9\u5305\u3002"
};
var DEFAULT_TRAINING_SESSION_CONFIG = {
  contentPack: "tcf",
  moduleId: "listening",
  intensity: "standard",
  ...TRAINING_INTENSITY_PRESETS.standard
};
var MIN_DIAGNOSIS_TRAINING_SESSIONS = 4;
var MIN_DIAGNOSIS_ANSWERED_QUESTIONS = 60;
function hasEntryAbilityDiagnosis(records, abilitySnapshots = [], examCode) {
  const scopedRecords = examCode ? filterTrainingRecordsByExamScope(records, examCode) : records;
  if (scopedRecords.some((record) => record.source === "diagnosis")) {
    return true;
  }
  const reportScope = examCode ? buildPlatformScope8({ examType: examCode }) : null;
  return abilitySnapshots.some((snapshot) => {
    if (snapshot.source !== "diagnosis") return false;
    if (!examCode) return true;
    if (snapshot.examType !== examCode) return false;
    if (!reportScope?.languageId || !snapshot.languageId) return true;
    return snapshot.languageId === reportScope.languageId;
  });
}
var EXAM_SELECTION_DISPLAY_ORDER = [
  "tcf",
  "ielts",
  "toefl",
  "jlpt",
  "topik",
  "dele",
  "siele"
];
function getExamSelectionCatalog() {
  const availableIds = getAllAvailableExams().map((exam) => exam.id).filter((id) => getExamConfig5(id) != null);
  const orderIndex = new Map(EXAM_SELECTION_DISPLAY_ORDER.map((id, index) => [id, index]));
  return [...availableIds].sort((a, b) => {
    const left = orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER;
    const right = orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (left !== right) return left - right;
    return a.localeCompare(b);
  }).map((id) => id);
}
function buildContentPackModel(examCode) {
  const scope = buildPlatformScope8({ examType: examCode });
  const label = examCode === "ielts" ? "IELTS \u5185\u5BB9" : `${scope?.examName ?? examCode.toUpperCase()} \u5185\u5BB9\u5305`;
  if (examCode === "tcf") {
    return TCF_CONTENT_PACK;
  }
  return {
    id: examCode,
    role: "currentContentPack",
    label,
    description: `${scope?.languageName ?? "\u76EE\u6807\u8BED\u8A00"} ${scope?.examName ?? examCode.toUpperCase()} \u7684\u8BAD\u7EC3\u5185\u5BB9\u5305\uFF0C\u590D\u7528\u5E73\u53F0\u5B66\u4E60\u95ED\u73AF\u5E76\u6309\u8003\u8BD5 section \u7EC4\u7EC7\u3002`
  };
}
function getExamSections(examCode) {
  return getExamConfig5(examCode)?.sections ?? [];
}
function getDefaultTrainingModuleId(examCode) {
  return getExamSections(examCode)[0]?.id ?? DEFAULT_TRAINING_SESSION_CONFIG.moduleId;
}
function buildTrainingSessionConfigForModule(examCode, moduleId) {
  return {
    contentPack: examCode,
    moduleId,
    intensity: "standard",
    ...TRAINING_INTENSITY_PRESETS.standard
  };
}
function getActiveLearningScope(viewer) {
  return buildPlatformScope8({ examType: viewer.activeExamSpace.examCode });
}
function buildExamSpaceHref(scope) {
  if (!scope) return "/workspace";
  const params = new URLSearchParams({
    languageId: scope.languageId,
    examType: scope.examType
  });
  return `/workspace?${params.toString()}`;
}
function buildLearnerSectionContentReadiness(examCode, accessibleBanks) {
  return getExamSections(examCode).map((section) => {
    const learnableBankCount = selectLearnerTrainingBankCandidates({
      banks: accessibleBanks,
      examType: examCode,
      sectionId: section.id
    }).length;
    return {
      sectionId: section.id,
      label: getLearningModuleLabel(examCode, section.id),
      learnableBankCount,
      status: learnableBankCount > 0 ? "ready" : "contentUnavailable"
    };
  });
}
function hasExamEntitlement(viewer, examCode) {
  return viewer.entitlements.examTypes.some(
    (examType) => examType.toLowerCase() === examCode.toLowerCase()
  );
}
var DIAGNOSIS_ENTRY_REDIRECT = "/workspace";
function buildRegisterExamSpaceAction(scope) {
  const redirectTarget = scope ? buildExamSpaceHref(scope) : DIAGNOSIS_ENTRY_REDIRECT;
  return {
    id: "create-account-for-exam-space",
    label: scope ? "\u5148\u6CE8\u518C" : "\u8FDB\u5165\u8003\u8BD5\u7A7A\u95F4",
    kind: "primary",
    href: `/register?redirect=${encodeURIComponent(redirectTarget)}`
  };
}
function buildExamSelectionHeroProof(mode, examName) {
  const path = ["\u5F00\u901A", "\u6478\u5E95", "\u6309\u5929\u7EC3"];
  const intensityLabel = "\u65E5\u5E38\u8BAD\u7EC3";
  const intensityValue = "\u968F\u65F6\u7EC3 \xB7 \u968F\u65F6\u505C";
  const floatMetricValue = "12";
  const floatMetricLabel = "\u9898 \xB7 \u9ED8\u8BA4\u7EC4";
  switch (mode) {
    case "visitor":
      return {
        nextStepLabel: "\u4E0B\u4E00\u6B65",
        nextStepDetail: "\u9009\u4E00\u4E2A\u8003\u8BD5\u7A7A\u95F4 \xB7 \u5148\u6478\u5E95",
        path,
        intensityLabel,
        intensityValue,
        sessionTitle: "ExamFlow \u8003\u8BD5\u7A7A\u95F4",
        sessionSubtitle: "\u5F00\u901A \u2192 \u6478\u5E95 \u2192 \u6309\u5929\u7EC3",
        metricLabel: "\u4ECA\u65E5\u8BAD\u7EC3",
        metricValue: "\u4E00\u7EC4\u5C31\u591F",
        floatMetricValue,
        floatMetricLabel
      };
    case "redeem":
      return {
        nextStepLabel: "\u4E0B\u4E00\u6B65",
        nextStepDetail: "\u5151\u6362\u5F00\u901A\u540E\u8FDB\u5165\u5BF9\u5E94\u7A7A\u95F4",
        path,
        intensityLabel,
        intensityValue,
        sessionTitle: "\u5F00\u901A\u8003\u8BD5\u7A7A\u95F4",
        sessionSubtitle: "\u5151\u6362\u540E\u8FDB\u5165\u5BF9\u5E94 Exam",
        metricLabel: "\u4E0B\u4E00\u6B65",
        metricValue: "\u53BB\u5151\u6362",
        floatMetricValue,
        floatMetricLabel
      };
    case "returning":
      return {
        nextStepLabel: "\u4E0B\u4E00\u6B65",
        nextStepDetail: `\u7EE7\u7EED ${examName} \xB7 \u4ECA\u65E5\u4E00\u7EC4`,
        path,
        intensityLabel,
        intensityValue,
        sessionTitle: `${examName} \u8003\u8BD5\u7A7A\u95F4`,
        sessionSubtitle: "\u4ECA\u65E5\u6700\u8BE5\u7EC3\u7684\u90A3\u4E00\u7EC4",
        metricLabel: "\u4E0B\u4E00\u6B65",
        metricValue: "\u7EE7\u7EED\u7EC3",
        floatMetricValue,
        floatMetricLabel
      };
    default: {
      const _exhaustive = mode;
      return _exhaustive;
    }
  }
}
function buildHomepageHeroPrimaryAction(viewer) {
  if (!viewer.isAuthenticated) {
    return buildRegisterExamSpaceAction();
  }
  if (viewer.activeExamSpace.entitlementStatus === "active") {
    return {
      ...buildActiveExamSpaceAction(viewer),
      id: "continue-training",
      label: "\u7EE7\u7EED\u8BAD\u7EC3"
    };
  }
  return {
    id: "redeem-exam-space",
    label: "\u5151\u6362\u8003\u8BD5\u7A7A\u95F4",
    kind: "primary",
    href: `/redeem?redirect=${encodeURIComponent(DIAGNOSIS_ENTRY_REDIRECT)}`
  };
}
function buildRedeemExamSpaceAction(scope) {
  const redirectTarget = scope ? buildExamSpaceHref(scope) : DIAGNOSIS_ENTRY_REDIRECT;
  return {
    id: "redeem-exam-space",
    label: "\u5151\u6362\u8003\u8BD5\u7A7A\u95F4",
    kind: "secondary",
    href: `/redeem?redirect=${encodeURIComponent(redirectTarget)}`
  };
}
function buildActiveExamSpaceAction(viewer) {
  const activeScope = buildPlatformScope8({ examType: viewer.activeExamSpace.examCode });
  const examLabel = activeScope?.examName ?? viewer.activeExamSpace.examName;
  return {
    id: `continue-${viewer.activeExamSpace.examCode}-space`,
    label: viewer.activeExamSpace.examCode === "tcf" ? "\u8FDB\u5165\u6211\u7684\u7A7A\u95F4" : `\u8FDB\u5165 ${examLabel} \u7A7A\u95F4`,
    kind: "primary",
    href: buildExamSpaceHref(activeScope)
  };
}
function buildExamSelectionPageModel(viewer) {
  const hasActiveExamSpace = viewer.activeExamSpace.entitlementStatus === "active";
  const activeExamCode = viewer.activeExamSpace.examCode;
  const primarySpaceAction = buildHomepageHeroPrimaryAction(viewer);
  const examCards = getExamSelectionCatalog().map((examCode) => {
    const scope = buildPlatformScope8({ examType: examCode });
    const context = getExamLanguageContext3(examCode);
    const hasThisExam = hasExamEntitlement(viewer, examCode);
    const isSelected = hasActiveExamSpace && activeExamCode === examCode;
    let status;
    let statusLabel;
    let primaryAction;
    if (isSelected) {
      status = "selected";
      statusLabel = "\u8BAD\u7EC3\u4E2D";
      primaryAction = {
        ...buildActiveExamSpaceAction(viewer),
        label: "\u8FDB\u5165\u7A7A\u95F4 \u2192"
      };
    } else if (hasThisExam) {
      status = "owned";
      statusLabel = "\u5DF2\u5F00\u901A";
      primaryAction = {
        id: `open-${examCode}-space`,
        label: "\u8FDB\u5165 \u2192",
        kind: "primary",
        href: buildExamSpaceHref(scope)
      };
    } else if (viewer.isAuthenticated) {
      status = "locked";
      statusLabel = "\u672A\u5F00\u901A";
      primaryAction = {
        ...buildRedeemExamSpaceAction(scope),
        label: "\u53BB\u5151\u6362 \u2192"
      };
    } else {
      status = "available";
      statusLabel = "\u53EF\u5F00\u901A";
      primaryAction = {
        id: `view-${examCode}-public`,
        label: "\u4E86\u89E3 \u2192",
        kind: "primary",
        href: `/exams/${examCode}`
      };
    }
    return {
      id: examCode,
      name: context?.examName ?? examCode.toUpperCase(),
      subtitle: examCode === "tcf" ? "Canada / France" : scope ? scope.languageName : "\u8003\u8BD5\u7A7A\u95F4",
      statusLabel,
      status,
      primaryAction
    };
  });
  const entitled = hasActiveExamSpace;
  const heroMode = entitled ? "returning" : viewer.isAuthenticated ? "redeem" : "visitor";
  const examName = viewer.activeExamSpace.examName;
  let heroEyebrow;
  let heroTitle;
  let heroTitleAccent;
  let heroDescription;
  switch (heroMode) {
    case "returning":
      heroEyebrow = `\u7EE7\u7EED \xB7 ${examName}`;
      heroTitle = "\u505A\u5B8C\u4ECA\u5929";
      heroTitleAccent = "\u6700\u8BE5\u7EC3\u7684\u90A3\u4E00\u7EC4";
      heroDescription = `\u56DE\u5230 ${examName} \u8003\u8BD5\u7A7A\u95F4\uFF0C\u4ECE\u4E0B\u4E00\u6B65\u5F00\u59CB\u3002\u6478\u5E95\u3001\u6309\u5929\u7EC3\u3001\u518D\u51B3\u5B9A\u6A21\u8003\u3002`;
      break;
    case "redeem":
      heroEyebrow = "\u5F00\u901A\u8003\u8BD5\u7A7A\u95F4";
      heroTitle = "\u5151\u6362\u5F00\u901A";
      heroTitleAccent = "\u8FDB\u5165\u5BF9\u5E94\u8003\u8BD5";
      heroDescription = "\u4E00\u6B21\u53EA\u76EF\u4E00\u4E2A\u8003\u8BD5\u7A7A\u95F4\uFF1A\u6478\u5E95\u770B\u6E05\u8D77\u70B9\uFF0C\u518D\u6309\u5929\u7EC3\u5B8C\u8BE5\u7EC3\u7684\u90A3\u4E00\u7EC4\u3002";
      break;
    case "visitor":
      heroEyebrow = "\u591A\u8BED\u8003\u8BD5\u8BAD\u7EC3";
      heroTitle = "\u4E00\u6B21\u53EA\u76EF\u4E00\u4E2A";
      heroTitleAccent = "\u8003\u8BD5\u7A7A\u95F4";
      heroDescription = "\u5F00\u901A\u540E\uFF1A\u6478\u5E95 \u2192 \u6309\u5929\u7EC3\u3002\u4E13\u9879\u8BAD\u7EC3\u968F\u65F6\u5F00\u968F\u65F6\u505C\uFF0C\u4E0D\u628A\u5B98\u65B9\u6A21\u8003\u9898\u91CF\u5F53\u6210\u65E5\u5E38\u3002";
      break;
    default: {
      const _exhaustive = heroMode;
      throw new Error(`Unhandled exam selection hero mode: ${String(_exhaustive)}`);
    }
  }
  return {
    viewer,
    hero: {
      mode: heroMode,
      eyebrow: heroEyebrow,
      brand: "ExamFlow",
      title: heroTitle,
      titleAccent: heroTitleAccent,
      description: heroDescription,
      primaryAction: {
        ...primarySpaceAction
      },
      secondaryAction: {
        id: "browse-exam-spaces",
        label: "\u6D4F\u89C8\u8003\u8BD5",
        kind: "secondary",
        href: "#exam-spaces"
      },
      proof: buildExamSelectionHeroProof(heroMode, examName)
    },
    exams: examCards,
    principles: [
      {
        title: "\u5F00\u901A",
        body: "\u5151\u6362\u540E\u8FDB\u5165\u5BF9\u5E94\u8003\u8BD5\u7A7A\u95F4\u3002"
      },
      {
        title: "\u6478\u5E95",
        body: "\u5148\u770B\u6E05\u4ECE\u54EA\u5F00\u59CB\u7EC3\u3002"
      },
      {
        title: "\u6309\u5929\u7EC3",
        body: "\u6BCF\u5929\u505A\u5B8C\u4E00\u7EC4\uFF0C\u518D\u51B3\u5B9A\u4E0B\u4E00\u6B65\u3002"
      }
    ]
  };
}
function buildUserLearningContext(user, entitlements, options = {}) {
  const activeScope = resolveActiveExamScope(options, entitlements);
  const examCode = activeScope.examType;
  const expiresAt = getEntitlementExpiry(entitlements, examCode);
  const hasAccess = canAccessExam(entitlements, examCode);
  const isTcf = examCode === "tcf";
  const examName = activeScope.examName;
  const displayName = isTcf ? "TCF Canada \u7EC3\u4E60\u7A7A\u95F4" : `${activeScope.examName} \u7EC3\u4E60\u7A7A\u95F4`;
  return {
    userId: user?.id ?? null,
    userName: user?.name || user?.email?.split("@")[0] || "\u540C\u5B66",
    isAuthenticated: Boolean(user),
    emailVerified: user?.emailVerified !== false,
    activeExamSpace: {
      id: `exam-space-${examCode}`,
      examCode,
      examName,
      displayName,
      targetLevel: null,
      targetDate: null,
      entitlementStatus: hasAccess ? "active" : "none",
      expiresAt
    },
    entitlements: {
      examTypes: entitlements?.examTypes ?? [],
      features: entitlements?.features ?? [],
      expiresAt
    }
  };
}
function resolveActiveExamScope(options, entitlements) {
  const requestedScope = buildPlatformScope8({
    languageId: options.languageId,
    examType: options.examType
  });
  const defaultExamType = chooseDefaultActiveExam(entitlements);
  const entitlementScope = defaultExamType ? buildPlatformScope8({ examType: defaultExamType }) : null;
  const fallbackScope = buildPlatformScope8({ examType: "tcf" });
  return requestedScope ?? entitlementScope ?? fallbackScope;
}
function buildTrainingLoop(current) {
  const steps = [
    { id: "diagnosis", label: "\u8BCA\u65AD" },
    { id: "content", label: "\u5185\u5BB9" },
    { id: "training", label: "\u4E13\u8BAD" },
    { id: "mockExam", label: "\u6A21\u8003" },
    { id: "update", label: "\u66F4\u65B0" }
  ];
  const currentIndex = steps.findIndex((step) => step.id === current);
  return steps.map((step, index) => ({
    ...step,
    status: index < currentIndex ? "done" : index === currentIndex ? "current" : "next"
  }));
}
function buildLearnerContentReadiness(viewer, input = {}) {
  const scope = buildPlatformScope8({ examType: viewer.activeExamSpace.examCode });
  const scopeLabel = scope ? `${scope.languageName} \xB7 ${scope.examName}` : viewer.activeExamSpace.examName;
  const workspaceHref = buildScopedLearningHref("/workspace", scope);
  if (!input.accessibleBanksStatus && !input.accessibleBanks) {
    return {
      status: "ready",
      title: "\u5185\u5BB9\u5DF2\u5C31\u7EEA",
      description: `${scopeLabel} \u5DF2\u6709\u53EF\u5B66\u5185\u5BB9\uFF0C\u53EF\u4EE5\u7EE7\u7EED\u8BCA\u65AD\u3001\u8BAD\u7EC3\u548C\u6A21\u8003\u95ED\u73AF\u3002`,
      tone: "success",
      scopeLabel,
      learnableBankCount: 1,
      sectionReadiness: [],
      missingSectionLabels: [],
      primaryAction: {
        id: "continue-learning-space",
        label: "\u7EE7\u7EED\u5B66\u4E60",
        kind: "primary",
        href: workspaceHref
      }
    };
  }
  if (!viewer.isAuthenticated || viewer.activeExamSpace.entitlementStatus !== "active") {
    return {
      status: "entitlementRequired",
      title: "\u5148\u5F00\u901A\u8003\u8BD5\u7A7A\u95F4",
      description: `${scopeLabel} \u9700\u8981\u6709\u6548\u6388\u6743\u540E\u624D\u80FD\u8FDB\u5165\u8BCA\u65AD\u3001\u8BAD\u7EC3\u548C\u6A21\u8003\u3002`,
      tone: "locked",
      scopeLabel,
      learnableBankCount: 0,
      sectionReadiness: [],
      missingSectionLabels: [],
      primaryAction: {
        id: "redeem-before-learning",
        label: "\u5151\u6362\u6388\u6743\u7801",
        kind: "primary",
        href: "/redeem?redirect=/workspace"
      }
    };
  }
  if (input.accessibleBanksStatus === "loading") {
    return {
      status: "checking",
      title: "\u6B63\u5728\u68C0\u67E5\u53EF\u5B66\u5185\u5BB9",
      description: `\u6B63\u5728\u786E\u8BA4 ${scopeLabel} \u662F\u5426\u5DF2\u6709\u5DF2\u53D1\u5E03\u4E14\u53EF\u5B66\u7684\u5185\u5BB9\u3002`,
      tone: "neutral",
      scopeLabel,
      learnableBankCount: 0,
      sectionReadiness: [],
      missingSectionLabels: [],
      primaryAction: {
        id: "checking-content-readiness",
        label: "\u68C0\u67E5\u4E2D",
        kind: "secondary",
        disabled: true
      }
    };
  }
  if (input.accessibleBanksStatus === "unavailable") {
    return {
      status: "checking",
      title: "\u6682\u65F6\u65E0\u6CD5\u786E\u8BA4\u53EF\u5B66\u5185\u5BB9",
      description: `\u6682\u65F6\u65E0\u6CD5\u786E\u8BA4 ${scopeLabel} \u662F\u5426\u5DF2\u6709\u5DF2\u53D1\u5E03\u4E14\u53EF\u5B66\u7684\u5185\u5BB9\uFF0C\u8BF7\u91CD\u8BD5\u3002`,
      tone: "warning",
      scopeLabel,
      learnableBankCount: 0,
      sectionReadiness: [],
      missingSectionLabels: [],
      primaryAction: {
        id: "content-readiness-unavailable",
        label: "\u91CD\u65B0\u68C0\u67E5",
        kind: "secondary"
      }
    };
  }
  const learnableBankCount = input.accessibleBanks == null ? 1 : selectLearnerTrainingBankCandidates({
    banks: input.accessibleBanks,
    examType: viewer.activeExamSpace.examCode
  }).length;
  const sectionReadiness = input.accessibleBanks == null ? [] : buildLearnerSectionContentReadiness(viewer.activeExamSpace.examCode, input.accessibleBanks);
  const missingSectionLabels = sectionReadiness.filter((section) => section.status === "contentUnavailable").map((section) => section.label);
  if (input.requireFullExamCoverage && learnableBankCount > 0 && missingSectionLabels.length > 0) {
    return {
      status: "contentUnavailable",
      title: "\u5168\u5957\u6A21\u8003\u5185\u5BB9\u672A\u8865\u9F50",
      description: `${scopeLabel} \u5DF2\u6709\u90E8\u5206\u53EF\u5B66\u5185\u5BB9\uFF0C\u4F46\u5168\u771F\u6A21\u8003\u8FD8\u7F3A\u5C11 ${missingSectionLabels.join("\u3001")}\u3002\u5148\u7EE7\u7EED\u4E13\u9879\u8BAD\u7EC3\uFF0C\u7B49\u5185\u5BB9\u8986\u76D6\u6240\u6709\u8003\u8BD5\u6A21\u5757\u540E\u518D\u5F00\u59CB\u6574\u5957\u6A21\u8003\u3002`,
      tone: "warning",
      scopeLabel,
      learnableBankCount,
      sectionReadiness,
      missingSectionLabels,
      primaryAction: {
        id: "continue-training-before-full-coverage",
        label: "\u7EE7\u7EED\u4E13\u9879\u8BAD\u7EC3",
        kind: "secondary",
        href: buildScopedLearningHref("/training", scope)
      }
    };
  }
  if (learnableBankCount > 0) {
    return {
      status: "ready",
      title: "\u5185\u5BB9\u5DF2\u5C31\u7EEA",
      description: `${scopeLabel} \u5DF2\u6709\u53EF\u5B66\u5185\u5BB9\uFF0C\u53EF\u4EE5\u7EE7\u7EED\u8BCA\u65AD\u3001\u8BAD\u7EC3\u548C\u6A21\u8003\u95ED\u73AF\u3002`,
      tone: "success",
      scopeLabel,
      learnableBankCount,
      sectionReadiness,
      missingSectionLabels,
      primaryAction: {
        id: "continue-learning-space",
        label: "\u7EE7\u7EED\u5B66\u4E60",
        kind: "primary",
        href: workspaceHref
      }
    };
  }
  return {
    status: "contentUnavailable",
    title: "\u6388\u6743\u5DF2\u5F00\u901A\uFF0C\u5185\u5BB9\u6B63\u5728\u51C6\u5907",
    description: `${scopeLabel} \u5DF2\u5728\u4F60\u7684\u6388\u6743\u8303\u56F4\u5185\uFF0C\u4F46\u5F53\u524D\u6CA1\u6709\u5DF2\u53D1\u5E03\u4E14\u53EF\u5B66\u7684\u5185\u5BB9\u3002\u5E73\u53F0\u9700\u8981\u5148\u8865\u9F50\u5185\u5BB9\u540E\u624D\u80FD\u5F00\u59CB\u8BAD\u7EC3\u3002`,
    tone: "warning",
    scopeLabel,
    learnableBankCount,
    sectionReadiness,
    missingSectionLabels,
    primaryAction: {
      id: "view-entitlement-without-content",
      label: "\u67E5\u770B\u6388\u6743\u72B6\u6001",
      kind: "secondary",
      href: "/profile"
    }
  };
}
function buildAbilityDiagnosisNextStep({
  status,
  prescription,
  primaryAction,
  noDiagnosis,
  hasAbilitySnapshot = false,
  hasEntryDiagnosis = false
}) {
  if (noDiagnosis) {
    return {
      id: "start-ability-diagnosis",
      title: "\u5148\u5B8C\u6210\u4E00\u6B21\u5165\u95E8\u8BCA\u65AD",
      description: "\u7528\u4E00\u8F6E\u8DE8\u79D1\u76EE\u62BD\u6837\u5EFA\u7ACB\u521D\u8BCA\u57FA\u7EBF\uFF1B\u4E5F\u53EF\u4EE5\u5148\u53BB\u4E13\u9879\uFF0C\u7EC3\u8FC7\u7684\u8BB0\u5F55\u540C\u6837\u8FDB\u5165\u8FD9\u91CC\u3002",
      tone: "current",
      action: primaryAction
    };
  }
  return {
    id: "follow-ability-prescription",
    title: `\u6309\u5904\u65B9\u8BAD\u7EC3${prescription.moduleLabel}`,
    description: status === "lowData" && hasEntryDiagnosis ? "\u521D\u8BCA\u5DF2\u51FA\u3002\u7A33\u5B9A\u5EA6\u8FD8\u4E0D\u591F\u65F6\uFF0C\u5148\u6309\u5904\u65B9\u7EC3\u4E13\u9879\uFF1B\u6837\u672C\u591F\u4E86\u5904\u65B9\u4F1A\u66F4\u7A33\u3002" : status === "lowData" && !hasAbilitySnapshot ? "\u5F53\u524D\u6837\u672C\u8FD8\u4E0D\u7A33\u5B9A\uFF0C\u5148\u6267\u884C\u63A8\u8350\u4E13\u9879\uFF0C\u8BA9\u62A5\u544A\u6709\u8DB3\u591F\u8BC1\u636E\u66F4\u65B0\u3002" : prescription.reason,
    tone: status === "ready" ? "current" : "warning",
    action: primaryAction
  };
}
function buildExamTrainingEntryPageModel(viewer, readinessInput = {}) {
  const contentReadiness = buildLearnerContentReadiness(viewer, readinessInput);
  const defaultModuleId = contentReadiness.sectionReadiness.find((section) => section.status === "ready")?.sectionId ?? getDefaultTrainingModuleId(viewer.activeExamSpace.examCode);
  const defaultConfig = buildTrainingSessionConfigForModule(
    viewer.activeExamSpace.examCode,
    defaultModuleId
  );
  const scope = buildPlatformScope8({ examType: viewer.activeExamSpace.examCode });
  const startHref = buildAttemptHref({
    kind: "training",
    languageId: scope?.languageId,
    examType: viewer.activeExamSpace.examCode,
    skillId: defaultConfig.moduleId,
    intensity: defaultConfig.intensity
  });
  const basePrimaryAction = contentReadiness.status === "ready" ? {
    id: "start-training-session",
    label: "\u5F00\u59CB\u7EC3",
    kind: "primary",
    href: startHref
  } : {
    ...contentReadiness.primaryAction,
    id: `training-${contentReadiness.primaryAction.id}`,
    disabled: contentReadiness.status === "checking" ? true : contentReadiness.primaryAction.disabled
  };
  return {
    viewer,
    contentPack: buildContentPackModel(viewer.activeExamSpace.examCode),
    contentReadiness,
    pageTitle: "\u9009\u62E9\u672C\u8F6E\u8BAD\u7EC3",
    pageSubtitle: "\u9009\u79D1\u76EE\u5F00\u7EC3\uFF0C\u968F\u65F6\u53EF\u505C\uFF1B\u5DF2\u7B54\u9898\u76EE\u5199\u5165\u80FD\u529B\u8BCA\u65AD\u8BC1\u636E\u3002",
    selectedModuleId: defaultConfig.moduleId,
    moduleOptions: buildTrainingModuleOptions(
      viewer.activeExamSpace.examCode,
      defaultConfig.moduleId,
      contentReadiness.sectionReadiness
    ),
    selectedContent: {
      title: `${getLearningModuleLabel(viewer.activeExamSpace.examCode, defaultConfig.moduleId)}\u4E13\u9879\u8BAD\u7EC3`,
      reason: "\u6CA1\u6709\u8DB3\u591F\u8BAD\u7EC3\u8BB0\u5F55\u65F6\u9ED8\u8BA4\u4ECE\u542C\u529B\u5F00\u59CB\uFF1B\u6709\u8BB0\u5F55\u540E\u4F1A\u6309\u6700\u8FD1\u8868\u73B0\u63A8\u8350\u8584\u5F31\u6A21\u5757\u3002",
      intensityLabel: `\u7EA6 ${defaultConfig.questionCount} \u9898 \xB7 \u968F\u65F6\u53EF\u505C`,
      completionLabel: "\u7EC3\u5B8C\u6216\u968F\u65F6\u7ED3\u675F\u540E\u5199\u5165\u80FD\u529B\u8BCA\u65AD\uFF0C\u5E76\u5224\u65AD\u662F\u5426\u5F00\u653E\u9636\u6BB5\u5C0F\u6D4B\u3002"
    },
    parameterMapping: [
      { label: "contentPack", value: defaultConfig.contentPack },
      { label: "section", value: defaultConfig.moduleId },
      { label: "source", value: "accessibleQuestionBanks" },
      { label: "intensity", value: "12q/24min" }
    ],
    platformSessionPreview: {
      engine: "\u771F\u5B9E\u9898\u5E93\u7EC3\u4E60",
      completionTarget: "\u5199\u5165\u80FD\u529B\u8BCA\u65AD"
    },
    primaryAction: {
      ...basePrimaryAction
    }
  };
}
function buildTcfTrainingEntryPageModel(viewer, readinessInput = {}) {
  return buildExamTrainingEntryPageModel(viewer, readinessInput);
}
function buildMockExamPageModel(viewer, status = "entry", trainingRecords = [], readinessInput = {}) {
  const scopedTrainingRecords = filterTrainingRecordsByExamScope(
    trainingRecords,
    viewer.activeExamSpace.examCode
  );
  const evidence = summarizeDiagnosisTrainingEvidence(
    scopedTrainingRecords,
    viewer.activeExamSpace.examCode
  );
  const contentReadiness = buildLearnerContentReadiness(viewer, {
    ...readinessInput,
    requireFullExamCoverage: true
  });
  const resolvedStatus = status === "entry" ? resolveMockExamPageStatus({
    records: scopedTrainingRecords,
    examCode: viewer.activeExamSpace.examCode
  }) : status;
  const stateConfig = getMockExamStateConfig(
    resolvedStatus,
    evidence,
    contentReadiness,
    getActiveLearningScope(viewer),
    viewer.activeExamSpace.examCode
  );
  return {
    viewer,
    contentReadiness,
    status: resolvedStatus,
    pageTitle: stateConfig.pageTitle,
    pageSubtitle: stateConfig.pageSubtitle,
    primaryAction: stateConfig.primaryAction,
    secondaryAction: stateConfig.secondaryAction,
    availability: stateConfig.availability,
    modules: buildFullMockModules(viewer.activeExamSpace.examCode),
    metrics: stateConfig.metrics,
    stateCard: stateConfig.stateCard
  };
}
function resolveMockExamPageStatus({
  records,
  examCode = "tcf"
}) {
  const evidence = summarizeDiagnosisTrainingEvidence(
    filterTrainingRecordsByExamScope(records, examCode),
    examCode
  );
  if (evidence.recordCount === 0) {
    return "noRecord";
  }
  if (!hasEnoughDiagnosisEvidence(evidence)) {
    return "unavailable";
  }
  return "entry";
}
function buildAbilityDiagnosisPageModel(viewer, status = "ready", trainingRecords = [], mockExamRecords = [], abilitySnapshots = [], options = {}) {
  const isUpdating = status === "updating";
  const isError = status === "updateError";
  const noDiagnosis = status === "noDiagnosis";
  const scopedTrainingRecords = filterTrainingRecordsByExamScope(
    trainingRecords,
    viewer.activeExamSpace.examCode
  );
  const evidence = summarizeDiagnosisTrainingEvidence(
    scopedTrainingRecords,
    viewer.activeExamSpace.examCode
  );
  const mockEvidence = summarizeMockExamEvidence(mockExamRecords, viewer.activeExamSpace.examCode);
  const latestSnapshot = getLatestAbilitySnapshot(abilitySnapshots);
  const hasEntryDiagnosis = hasEntryAbilityDiagnosis(
    scopedTrainingRecords,
    abilitySnapshots,
    viewer.activeExamSpace.examCode
  );
  const sortedSnapshots = [...abilitySnapshots].sort(
    (a, b) => toSnapshotTime(a.createdAt) - toSnapshotTime(b.createdAt)
  );
  const weakestSnapshotDimension = getWeakestAbilitySnapshotDimension(latestSnapshot);
  const snapshotPrescription = resolveTrainingPrescriptionModuleId({
    trainingEvidence: evidence,
    abilitySnapshots
  });
  const prescriptionModuleId = weakestSnapshotDimension ? snapshotPrescription.moduleId : evidence.recordCount === 0 && mockEvidence.weakestModuleId ? mockEvidence.weakestModuleId : evidence.weakestModuleId;
  const prescription = buildDefaultPrescription(
    viewer.activeExamSpace.examCode,
    prescriptionModuleId,
    weakestSnapshotDimension ? snapshotPrescription.reason : evidence.recordCount === 0 ? mockEvidence.weakestModuleId ? `${mockEvidence.weakestModuleLabel}\u662F\u6700\u8FD1\u6A21\u8003\u91CC\u6700\u9700\u8981\u8F6C\u5316\u4E3A\u4E13\u9879\u8BAD\u7EC3\u7684\u6A21\u5757\u3002` : "\u8FD8\u6CA1\u6709\u8BAD\u7EC3\u8BB0\u5F55\uFF0C\u5148\u5B8C\u6210\u4E00\u7EC4\u4E13\u9879\u8BAD\u7EC3\u5EFA\u7ACB\u80FD\u529B\u57FA\u7EBF\u3002" : `${evidence.weakestModuleLabel}\u662F\u5F53\u524D\u8BAD\u7EC3\u8BB0\u5F55\u91CC\u6700\u9700\u8981\u7EE7\u7EED\u9A8C\u8BC1\u7684\u6A21\u5757\u3002`
  );
  const reportPlatformScope = buildPlatformScope8({ examType: viewer.activeExamSpace.examCode });
  const mockExamHref = buildScopedLearningHref("/mock-exam", reportPlatformScope);
  const diagnosisEntryAction = buildDiagnosisEntryAction(
    viewer.activeExamSpace.examCode,
    reportPlatformScope,
    { readySectionIds: options.readyDiagnosisSectionIds ?? null }
  );
  const primaryAction = noDiagnosis ? {
    ...diagnosisEntryAction,
    disabled: isUpdating || Boolean(diagnosisEntryAction.disabled)
  } : {
    ...prescription.primaryAction,
    id: "start-prescription",
    label: "\u5F00\u59CB\u63A8\u8350\u8BAD\u7EC3",
    disabled: isUpdating
  };
  const secondaryAction = !noDiagnosis && hasEntryDiagnosis ? {
    ...diagnosisEntryAction,
    id: "rerun-entry-diagnosis",
    label: "\u518D\u8DD1\u5165\u95E8\u8BCA\u65AD",
    kind: "secondary",
    disabled: isUpdating || Boolean(diagnosisEntryAction.disabled)
  } : {
    id: "return-to-workspace",
    label: isError ? "\u91CD\u8BD5\u8BCA\u65AD\u66F4\u65B0" : "\u8FD4\u56DE\u5DE5\u4F5C\u53F0",
    kind: isError ? "danger" : "secondary",
    href: buildExamSpaceHref(reportPlatformScope)
  };
  const writingBand = buildAbilityWritingEvidenceBand(
    viewer.activeExamSpace.examCode,
    scopedTrainingRecords
  );
  return {
    viewer,
    status,
    abilityReport: {
      id: `ability-report-${viewer.activeExamSpace.examCode}`,
      platformScope: reportPlatformScope,
      recommendedTrainingScope: prescription.platformScope,
      scopeLabel: formatPlatformScopeLabel(reportPlatformScope),
      evidenceLabel: formatAbilityEvidenceLabel(evidence, mockEvidence)
    },
    pageTitle: noDiagnosis ? "\u5148\u5B8C\u6210\u4E00\u6B21\u5165\u95E8\u8BCA\u65AD" : isUpdating ? "\u8BCA\u65AD\u6B63\u5728\u66F4\u65B0" : status === "lowData" && hasEntryDiagnosis ? "\u521D\u8BCA\u5DF2\u51FA\uFF0C\u7EE7\u7EED\u79EF\u7D2F\u7A33\u5B9A\u6837\u672C" : "\u80FD\u529B\u8BCA\u65AD\u7ED9\u51FA\u4E0B\u4E00\u6B65\u8BAD\u7EC3",
    pageSubtitle: hasEntryDiagnosis ? "\u5165\u95E8\u8BCA\u65AD\u5EFA\u7ACB\u521D\u8BCA\u57FA\u7EBF\uFF1B\u7A33\u5B9A\u5EA6\u968F\u4E13\u9879/\u6A21\u8003\u6837\u672C\u5347\u9AD8\u540E\uFF0C\u5904\u65B9\u66F4\u53EF\u4FE1\u3002\u80FD\u529B\u8BCA\u65AD\u672C\u8EAB\u4E0D\u6D88\u8017 AI \u914D\u989D\u3002" : "\u4E13\u9879\u8BAD\u7EC3\u548C\u6A21\u62DF\u8003\u8BD5\u4E0D\u8FDB\u5165\u72EC\u7ACB\u7ED3\u679C\u4E2D\u5FC3\uFF0C\u8868\u73B0\u4F1A\u805A\u5408\u6210\u8FD9\u91CC\u7684\u80FD\u529B\u7ED3\u8BBA\u548C\u8BAD\u7EC3\u5904\u65B9\u3002",
    nextStep: buildAbilityDiagnosisNextStep({
      status,
      prescription,
      primaryAction,
      noDiagnosis,
      hasAbilitySnapshot: latestSnapshot !== null,
      hasEntryDiagnosis
    }),
    primaryAction,
    secondaryAction,
    snapshot: {
      displayLevel: latestSnapshot ? latestSnapshot.level ?? String(latestSnapshot.overallScore) : noDiagnosis ? "\u672A\u6D4B" : status === "lowData" && hasEntryDiagnosis ? "\u521D\u8BCA" : status === "lowData" ? "\u7F3A\u6837\u672C" : "\u6682\u4F30",
      stabilityLabel: noDiagnosis ? "\u7B49\u5F85\u5165\u95E8\u8BCA\u65AD" : evidence.recordCount === 0 && mockEvidence.recordCount > 0 ? "\u6A21\u8003\u8BC1\u636E\u5DF2\u8FDB\u5165\u8BCA\u65AD \xB7 \u4E13\u9879\u6837\u672C\u4E0D\u8DB3" : status === "lowData" && hasEntryDiagnosis ? `\u521D\u8BCA\u5DF2\u51FA \xB7 \u7A33\u5B9A\u5EA6 ${evidence.answeredCount}/${MIN_DIAGNOSIS_ANSWERED_QUESTIONS} \u9898 \xB7 ${evidence.recordCount}/${MIN_DIAGNOSIS_TRAINING_SESSIONS} \u7EC4` : status === "lowData" ? `\u6837\u672C\u4E0D\u8DB3 \xB7 ${evidence.answeredCount}/${MIN_DIAGNOSIS_ANSWERED_QUESTIONS} \u9898` : evidence.recordCount > 0 ? `\u57FA\u4E8E ${evidence.recordCount} \u7EC4\u8BAD\u7EC3\u8BB0\u5F55` : "\u7B49\u5F85\u8BAD\u7EC3\u8BB0\u5F55",
      conclusion: noDiagnosis ? "\u5B8C\u6210\u4E00\u6B21\u5165\u95E8\u8BCA\u65AD\u540E\u663E\u793A\u521D\u8BCA\u7B49\u7EA7\u3002" : hasEntryDiagnosis && status === "lowData" ? `${evidence.weakestModuleLabel}\u662F\u521D\u8BCA\u540E\u6700\u9700\u8981\u9A8C\u8BC1\u7684\u6A21\u5757\uFF1B\u518D\u79EF\u7D2F\u6837\u672C\u53EF\u63D0\u9AD8\u7A33\u5B9A\u5EA6\u3002` : evidence.recordCount > 0 ? `${evidence.weakestModuleLabel}\u662F\u5F53\u524D\u8BAD\u7EC3\u8BC1\u636E\u91CC\u6700\u9700\u8981\u7EE7\u7EED\u9A8C\u8BC1\u7684\u6A21\u5757\u3002` : mockEvidence.recordCount > 0 ? `${mockEvidence.latestResultLabel}\uFF0C${mockEvidence.weakestModuleLabel ?? "\u8584\u5F31\u6A21\u5757"}\u9700\u8981\u5148\u8F6C\u5316\u4E3A\u4E13\u9879\u8BAD\u7EC3\u5904\u65B9\u3002` : "\u5B8C\u6210\u51E0\u7EC4\u4E13\u9879\u8BAD\u7EC3\u540E\uFF0C\u8FD9\u91CC\u4F1A\u6309\u5404\u79D1\u8868\u73B0\u7ED9\u51FA\u80FD\u529B\u7EF4\u5EA6\u3002",
      evidenceLabel: formatAbilityEvidenceLabel(evidence, mockEvidence)
    },
    mockExamEvidence: mockEvidence,
    prescription,
    writingEvidence: writingBand ? {
      sampleCount: writingBand.summary.sampleCount,
      reportLine: writingBand.summary.reportLine,
      focusReason: writingBand.summary.focusReason,
      recommendedTaskNumber: writingBand.summary.recommendedTaskNumber,
      entryHref: writingBand.entryHref,
      actionLabel: writingBand.actionLabel
    } : null,
    dimensions: buildAbilityDimensions(
      evidence,
      latestSnapshot,
      viewer.activeExamSpace.examCode
    ),
    trend: sortedSnapshots.map((snapshot) => ({
      createdAt: snapshot.createdAt,
      overallScore: snapshot.overallScore,
      source: snapshot.source,
      level: snapshot.level
    })),
    updatePolicy: {
      nextUpdateLabel: isUpdating ? "\u9884\u8BA1 2 \u5206\u949F\u5185\u5B8C\u6210" : hasEntryDiagnosis && status === "lowData" ? `\u521D\u8BCA\u5DF2\u51FA \xB7 \u518D\u7EA6 ${evidence.remainingSessionCount} \u7EC4\u4E13\u9879\u53EF\u63D0\u9AD8\u7A33\u5B9A\u5EA6` : evidence.recordCount === 0 && mockEvidence.recordCount > 0 ? "\u6A21\u8003\u5DF2\u8FDB\u5165\u80FD\u529B\u8BCA\u65AD\uFF0C\u5B8C\u6210\u4E0B\u4E00\u7EC4\u4E13\u9879\u540E\u5237\u65B0\u5904\u65B9" : evidence.recordCount > 0 ? `\u8FD8\u9700 ${evidence.remainingSessionCount} \u7EC4\u4E13\u9879\u6216 1 \u6B21\u6A21\u8003\u540E\u66F4\u65B0` : "\u5B8C\u6210\u5165\u95E8\u8BCA\u65AD\u6216 4 \u7EC4\u4E13\u9879 / 1 \u6B21\u6A21\u8003\u540E\u66F4\u65B0",
      requiredEvidenceLabel: hasEntryDiagnosis ? `\u7A33\u5B9A\u5EA6\uFF1A\u81F3\u5C11 ${MIN_DIAGNOSIS_TRAINING_SESSIONS} \u7EC4\u8BAD\u7EC3\u4E0E ${MIN_DIAGNOSIS_ANSWERED_QUESTIONS} \u9898\u6709\u6548\u4F5C\u7B54\uFF08\u5165\u95E8\u8BCA\u65AD\u5DF2\u8BA1\u5165\uFF09\u3002` : `\u81F3\u5C11 ${MIN_DIAGNOSIS_ANSWERED_QUESTIONS} \u9898\u6709\u6548\u4F5C\u7B54\u540E\u63D0\u9AD8\u7A33\u5B9A\u5EA6\u3002`
    }
  };
}
function resolveAbilityDiagnosisPageStatus({
  requestedStatus,
  records,
  mockExamRecords = [],
  abilitySnapshots = [],
  examCode = "tcf"
}) {
  if (requestedStatus) {
    return requestedStatus;
  }
  const scopedRecords = filterTrainingRecordsByExamScope(records, examCode);
  const evidence = summarizeDiagnosisTrainingEvidence(scopedRecords, examCode);
  const reportScope = buildPlatformScope8({ examType: examCode });
  const scopedMockExamRecords = mockExamRecords.filter(
    (record) => mockExamRecordMatchesScope(record, reportScope, examCode)
  );
  const entryDiagnosis = hasEntryAbilityDiagnosis(scopedRecords, abilitySnapshots, examCode);
  if (evidence.recordCount === 0) {
    if (scopedMockExamRecords.length > 0 || entryDiagnosis) {
      return "lowData";
    }
    return "noDiagnosis";
  }
  if (evidence.recordCount < MIN_DIAGNOSIS_TRAINING_SESSIONS || evidence.answeredCount < MIN_DIAGNOSIS_ANSWERED_QUESTIONS) {
    return "lowData";
  }
  return "ready";
}
function buildExecutableTrainingPrescription({
  contentPack = "tcf",
  moduleId = "listening",
  intensity = "standard",
  reason,
  taskNumber
} = {}) {
  const moduleLabel = getLearningModuleLabel(contentPack, moduleId);
  const preset = resolveSessionSizeForSkill(moduleId, intensity);
  const platformScope = buildPlatformScope8({
    examType: contentPack,
    skillId: moduleId
  });
  const languageId = platformScope?.languageId ?? buildPlatformScope8({ examType: contentPack })?.languageId ?? "french";
  const launchHref = buildTrainingEntryHref({
    languageId,
    examType: contentPack,
    skillId: moduleId,
    intensityHint: intensity,
    taskNumber: moduleId === "writing" ? taskNumber : void 0
  });
  const isWriting = moduleId === "writing";
  return {
    id: `${contentPack}-${moduleId}-${intensity}`,
    moduleId,
    platformScope,
    moduleLabel,
    prescriptionLabel: platformScope ? `${platformScope.languageName} \xB7 ${platformScope.examName} \xB7 ${moduleLabel}` : moduleLabel,
    title: isWriting ? "\u5199\u4E00\u7BC7 \xB7 \u6279\u6539 \xB7 \u518D\u6539\u4E00\u7248" : moduleId === "listening" && contentPack === "tcf" ? "\u6570\u5B57\u3001\u65E5\u671F\u4E0E\u65F6\u95F4\u53D8\u5316" : `${moduleLabel}\u4E13\u9879\u5DE9\u56FA`,
    reason: reason ?? (isWriting ? "\u7528\u4E00\u7BC7\u4EFB\u52A1\u7EC3\u5B8C\u300C\u5199\u2192\u8BC4\u2192\u6539\u300D\u95ED\u73AF\uFF0C\u6BD4\u5237\u9009\u62E9\u9898\u66F4\u80FD\u63D0\u5347\u4E66\u9762\u8868\u8FBE\u3002" : `${moduleLabel}\u662F\u5F53\u524D\u6700\u5F71\u54CD\u9636\u6BB5\u6A21\u8003\u5F00\u653E\u7684\u8584\u5F31\u70B9\u3002`),
    durationMinutes: preset.durationMinutes,
    questionCount: preset.questionCount,
    launchHref,
    sessionHref: launchHref,
    primaryAction: {
      id: `start-${moduleId}-training`,
      label: isWriting ? "\u5199\u4E00\u7BC7\uFF08AI \u6279\u6539\uFF09" : "\u8FDB\u5165\u4E13\u9879\u8BAD\u7EC3",
      kind: "primary",
      href: launchHref
    }
  };
}
function buildDefaultPrescription(contentPack = "tcf", moduleId = "listening", reason) {
  return buildExecutableTrainingPrescription({ contentPack, moduleId, reason });
}
function formatPlatformScopeLabel(scope) {
  if (!scope) return "\u672A\u7ED1\u5B9A\u8003\u8BD5\u8303\u56F4";
  return `${scope.languageName} \xB7 ${scope.examName}`;
}
function filterTrainingRecordsByExamScope(records, examCode) {
  const reportScope = buildPlatformScope8({ examType: examCode });
  return records.filter((record) => trainingRecordMatchesExamScope(record, reportScope, examCode));
}
function trainingRecordMatchesExamScope(record, reportScope, fallbackExamCode) {
  if (record.platformScope && reportScope) {
    return record.platformScope.languageId === reportScope.languageId && record.platformScope.examType === reportScope.examType;
  }
  if (record.languageId && reportScope) {
    return record.languageId === reportScope.languageId && String(record.examType ?? record.contentPack).toLowerCase() === reportScope.examType;
  }
  return String(record.examType ?? record.contentPack).toLowerCase() === fallbackExamCode;
}
function hasEnoughDiagnosisEvidence(evidence) {
  return evidence.recordCount >= MIN_DIAGNOSIS_TRAINING_SESSIONS && evidence.answeredCount >= MIN_DIAGNOSIS_ANSWERED_QUESTIONS;
}
function getLatestAbilitySnapshot(abilitySnapshots) {
  if (abilitySnapshots.length === 0) return null;
  const sorted = [...abilitySnapshots].sort(
    (a, b) => toSnapshotTime(a.createdAt) - toSnapshotTime(b.createdAt)
  );
  return sorted[sorted.length - 1] ?? null;
}
function getWeakestAbilitySnapshotDimension(snapshot) {
  if (!snapshot || snapshot.dimensions.length === 0) return null;
  return [...snapshot.dimensions].sort(
    (a, b) => a.accuracy - b.accuracy || b.answeredCount - a.answeredCount
  )[0] ?? null;
}
function resolveTrainingPrescriptionModuleId({
  trainingEvidence,
  abilitySnapshots = []
}) {
  const latestSnapshot = getLatestAbilitySnapshot(abilitySnapshots);
  const weakestSnapshotDimension = getWeakestAbilitySnapshotDimension(latestSnapshot);
  if (weakestSnapshotDimension) {
    return {
      moduleId: normalizeTcfModuleId(weakestSnapshotDimension.sectionId),
      reason: `${weakestSnapshotDimension.label ?? weakestSnapshotDimension.sectionId}\u662F\u6700\u8FD1\u4E00\u6B21\u80FD\u529B\u5FEB\u7167\u91CC\u6700\u9700\u8981\u7EE7\u7EED\u9A8C\u8BC1\u7684\u6A21\u5757\uFF08\u6B63\u786E\u7387 ${weakestSnapshotDimension.accuracy}%\uFF09\u3002`,
      fromSnapshot: true
    };
  }
  return {
    moduleId: trainingEvidence.weakestModuleId,
    reason: trainingEvidence.recordCount === 0 ? "\u8FD8\u6CA1\u6709\u8BAD\u7EC3\u8BB0\u5F55\uFF0C\u5148\u5B8C\u6210\u4E00\u7EC4\u6807\u51C6\u8BAD\u7EC3\u5EFA\u7ACB\u80FD\u529B\u57FA\u7EBF\u3002" : `${trainingEvidence.weakestModuleLabel}\u662F\u5F53\u524D\u8BAD\u7EC3\u8BB0\u5F55\u91CC\u6700\u9700\u8981\u7EE7\u7EED\u9A8C\u8BC1\u7684\u6A21\u5757\u3002`,
    fromSnapshot: false
  };
}
var MOCK_DIGESTION_WINDOW_MS = 48 * 60 * 60 * 1e3;
function isInMockDigestionWindow(latestSnapshot, nowMs = Date.now(), windowMs = MOCK_DIGESTION_WINDOW_MS) {
  if (!latestSnapshot || latestSnapshot.source !== "mock") {
    return false;
  }
  const createdAt = toSnapshotTime(latestSnapshot.createdAt);
  if (!Number.isFinite(createdAt) || createdAt <= 0) {
    return false;
  }
  return nowMs - createdAt >= 0 && nowMs - createdAt < windowMs;
}
function formatTrainingRecordTime(completedAt) {
  if (!Number.isFinite(completedAt)) {
    return "\u672A\u77E5\u65F6\u95F4";
  }
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(completedAt));
}
function summarizeDiagnosisTrainingEvidence(records, examCode) {
  const standardRecords = records.filter(isStandardDiagnosisTrainingRecord);
  const remediationRecordCount = records.length - standardRecords.length;
  const answeredCount = standardRecords.reduce((total, record) => total + record.answeredCount, 0);
  const sectionIds = getExamSections(examCode).map((section) => section.id);
  const moduleIds = sectionIds.length > 0 ? sectionIds : [];
  if (moduleIds.length === 0) {
    return {
      recordCount: standardRecords.length,
      remediationRecordCount,
      answeredCount,
      weakestModuleId: getDefaultTrainingModuleId(examCode),
      weakestModuleLabel: getLearningModuleLabel(examCode, getDefaultTrainingModuleId(examCode)),
      remainingSessionCount: Math.max(0, MIN_DIAGNOSIS_TRAINING_SESSIONS - standardRecords.length),
      evidenceLabel: standardRecords.length === 0 ? "\u5C1A\u65E0\u4E13\u9879\u8BAD\u7EC3\u6837\u672C" : `${standardRecords.length} \u7EC4\u4E13\u9879 \xB7 \u8003\u8BD5\u7ED3\u6784\u672A\u914D\u7F6E`,
      moduleSummaries: []
    };
  }
  const moduleSummaries = moduleIds.map((moduleId) => {
    const moduleRecords = standardRecords.filter((record) => record.moduleId === moduleId);
    const moduleAnsweredCount = moduleRecords.reduce(
      (total, record) => total + record.answeredCount,
      0
    );
    const averageAccuracy = moduleRecords.length > 0 ? Math.round(
      moduleRecords.reduce((total, record) => total + record.accuracy, 0) / moduleRecords.length
    ) : null;
    return {
      moduleId,
      label: getLearningModuleLabel(examCode, moduleId),
      averageAccuracy,
      answeredCount: moduleAnsweredCount
    };
  });
  const weakestClosed = moduleSummaries.filter(
    (summary) => summary.averageAccuracy !== null && !isOpenResponseSkillId(summary.moduleId)
  ).sort((a, b) => (a.averageAccuracy ?? 100) - (b.averageAccuracy ?? 100))[0] ?? null;
  const weakestModule = weakestClosed ?? moduleSummaries.find((summary) => !isOpenResponseSkillId(summary.moduleId)) ?? moduleSummaries[0];
  const remainingSessionCount = Math.max(
    0,
    MIN_DIAGNOSIS_TRAINING_SESSIONS - standardRecords.length,
    Math.ceil((MIN_DIAGNOSIS_ANSWERED_QUESTIONS - answeredCount) / 12)
  );
  const latestRecord = standardRecords.slice().sort((a, b) => b.completedAt - a.completedAt)[0];
  const baseEvidenceLabel = standardRecords.length > 0 ? `\u6765\u81EA ${standardRecords.length} \u7EC4\u4E13\u9879\u8BAD\u7EC3\u3001${answeredCount} \u9898\u6709\u6548\u4F5C\u7B54\u3002\u6700\u8FD1\u4E00\u6B21\uFF1A${latestRecord?.moduleLabel ?? weakestModule?.label ?? "\u8BAD\u7EC3"}\u3002` : "\u8FD8\u6CA1\u6709\u6807\u51C6\u4E13\u9879\u8BAD\u7EC3\u8BB0\u5F55\u3002\u5B8C\u6210\u8BCA\u65AD\u6216\u4E13\u9879\u8BAD\u7EC3\u540E\u4F1A\u751F\u6210\u80FD\u529B\u8BC1\u636E\u3002";
  const remediationEvidenceLabel = remediationRecordCount > 0 ? ` \u53E6\u6709 ${remediationRecordCount} \u7EC4\u9519\u9898\u590D\u76D8\u8BAD\u7EC3\u8BC1\u636E\u3002` : "";
  return {
    recordCount: standardRecords.length,
    remediationRecordCount,
    answeredCount,
    weakestModuleId: weakestModule?.moduleId ?? moduleIds[0] ?? "listening",
    weakestModuleLabel: weakestModule?.label ?? getLearningModuleLabel(examCode, moduleIds[0] ?? "listening"),
    remainingSessionCount,
    evidenceLabel: `${baseEvidenceLabel}${remediationEvidenceLabel}`,
    moduleSummaries
  };
}
function isStandardDiagnosisTrainingRecord(record) {
  return record.source !== "remediation";
}
function isRemediationTrainingRecord(record) {
  return record.source === "remediation";
}
function summarizeMockExamEvidence(records, examCode) {
  const reportScope = buildPlatformScope8({ examType: examCode });
  const scopeLabel = formatPlatformScopeLabel(reportScope);
  const scopedRecords = records.filter((record) => mockExamRecordMatchesScope(record, reportScope, examCode)).sort((a, b) => b.completedAt - a.completedAt);
  const latestRecord = scopedRecords[0];
  if (!latestRecord) {
    return {
      recordCount: 0,
      scopeLabel,
      latestResultLabel: "\u6682\u65E0\u6A21\u8003\u8BB0\u5F55",
      scoreLabel: "0 / 0",
      evidenceLabel: "\u6682\u65E0\u6A21\u8003\u8BC1\u636E\u3002",
      weakestModuleId: null,
      weakestModuleLabel: null
    };
  }
  const accuracy = latestRecord.totalQuestions > 0 ? Math.round(latestRecord.correctCount / latestRecord.totalQuestions * 100) : 0;
  const weakestSection = summarizeWeakestMockExamSection(latestRecord, examCode);
  return {
    recordCount: scopedRecords.length,
    scopeLabel,
    latestResultLabel: `\u6700\u8FD1\u6A21\u8003\u6B63\u786E\u7387 ${accuracy}%`,
    scoreLabel: `${latestRecord.correctCount} / ${latestRecord.totalQuestions}`,
    evidenceLabel: `\u6765\u81EA ${scopeLabel} ${scopedRecords.length} \u6B21\u6A21\u8003\uFF0C${accuracy}% \u6700\u8FD1\u6B63\u786E\u7387\u3002`,
    weakestModuleId: weakestSection?.moduleId ?? null,
    weakestModuleLabel: weakestSection?.moduleLabel ?? null
  };
}
function summarizeWeakestMockExamSection(record, examCode) {
  const summaries = /* @__PURE__ */ new Map();
  if (record.dimensions && record.dimensions.length > 0) {
    for (const dimension of record.dimensions) {
      if (!dimension.sectionId || dimension.answeredCount <= 0) continue;
      summaries.set(dimension.sectionId, {
        moduleId: dimension.sectionId,
        answeredCount: dimension.answeredCount,
        correctCount: dimension.correctCount
      });
    }
  } else {
    for (const answer of record.userAnswers ?? []) {
      if (!answer.sectionId) continue;
      const current = summaries.get(answer.sectionId) ?? {
        moduleId: answer.sectionId,
        answeredCount: 0,
        correctCount: 0
      };
      current.answeredCount += 1;
      current.correctCount += answer.isCorrect ? 1 : 0;
      summaries.set(answer.sectionId, current);
    }
  }
  return Array.from(summaries.values()).filter((summary) => summary.answeredCount > 0).map((summary) => ({
    moduleId: summary.moduleId,
    moduleLabel: getLearningModuleLabel(examCode, summary.moduleId),
    accuracy: Math.round(summary.correctCount / summary.answeredCount * 100),
    answeredCount: summary.answeredCount
  })).sort((a, b) => a.accuracy - b.accuracy || b.answeredCount - a.answeredCount)[0];
}
function mockExamRecordMatchesScope(record, reportScope, fallbackExamCode) {
  if (record.platformScope && reportScope) {
    return record.platformScope.languageId === reportScope.languageId && record.platformScope.examType === reportScope.examType;
  }
  return record.examType.toLowerCase() === fallbackExamCode;
}
function formatAbilityEvidenceLabel(trainingEvidence, mockEvidence) {
  if (trainingEvidence.recordCount > 0 && mockEvidence.recordCount > 0) {
    return `${trainingEvidence.evidenceLabel} \u53E6\u6709${mockEvidence.evidenceLabel}`;
  }
  if (trainingEvidence.recordCount > 0) {
    return trainingEvidence.evidenceLabel;
  }
  if (mockEvidence.recordCount > 0) {
    return mockEvidence.evidenceLabel;
  }
  return trainingEvidence.evidenceLabel;
}
function toSnapshotTime(createdAt) {
  if (typeof createdAt === "number") return createdAt;
  const parsed = Date.parse(createdAt);
  if (!Number.isNaN(parsed)) return parsed;
  const numeric = Number(createdAt);
  return Number.isFinite(numeric) ? numeric : 0;
}
function buildAbilityDimensions(evidence, latestSnapshot = null, examCode = "tcf") {
  if (latestSnapshot && latestSnapshot.dimensions.length > 0) {
    const weakest = [...latestSnapshot.dimensions].sort(
      (a, b) => a.accuracy - b.accuracy || b.answeredCount - a.answeredCount
    )[0];
    return latestSnapshot.dimensions.map((dimension) => {
      const isWeakest = dimension.sectionId === weakest.sectionId;
      const value = dimension.accuracy;
      return {
        label: dimension.label ?? dimension.sectionId,
        value,
        max: 100,
        tone: isWeakest ? "warning" : value >= 80 ? "success" : "current",
        note: isWeakest ? "\u4F18\u5148\u8BAD\u7EC3" : "\u4FDD\u6301"
      };
    });
  }
  if (evidence.recordCount === 0) {
    const sections = getExamSections(examCode);
    if (sections.length === 0) {
      return [];
    }
    return sections.map((section) => ({
      label: getLearningModuleLabel(examCode, section.id),
      value: 0,
      max: 100,
      tone: "locked",
      note: "\u7B49\u5F85\u6837\u672C"
    }));
  }
  return evidence.moduleSummaries.map((summary) => {
    const value = summary.averageAccuracy ?? 0;
    const isWeakest = summary.moduleId === evidence.weakestModuleId;
    return {
      label: summary.label,
      value,
      max: 100,
      tone: isWeakest ? "warning" : value >= 80 ? "success" : "current",
      note: summary.averageAccuracy === null ? "\u7B49\u5F85\u6837\u672C" : isWeakest ? "\u4F18\u5148\u8BAD\u7EC3" : "\u4FDD\u6301"
    };
  });
}
function normalizeTcfModuleId(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized.length > 0) {
    return normalized;
  }
  return DEFAULT_TRAINING_SESSION_CONFIG.moduleId;
}
function getLearningModuleLabel(examCode, moduleId) {
  const sectionName = getExamSections(examCode).find((section) => section.id === moduleId)?.name;
  if (sectionName) {
    return sectionName.replace(/\s*\([^)]*\)\s*$/u, "").trim() || sectionName;
  }
  return getSkillSectionLabel(moduleId);
}
function buildTrainingModuleOptions(examCode, selectedModuleId, sectionReadiness = []) {
  const readinessBySection = new Map(
    sectionReadiness.map((section) => [section.sectionId, section])
  );
  return getExamSections(examCode).map((section) => ({
    ...(() => {
      const readiness = readinessBySection.get(section.id);
      const status = readiness?.status ?? "ready";
      const label = getLearningModuleLabel(examCode, section.id);
      const runtimeBlockReason = resolveOpenResponseRuntimeBlockReason(section.id);
      const contentDisabled = status !== "ready";
      const disabled = contentDisabled || runtimeBlockReason != null;
      const openResponse = isOpenResponseSkillId(section.id);
      const trainingPreset = openResponse ? OPEN_RESPONSE_SESSION_PRESET : TRAINING_INTENSITY_PRESETS.standard;
      return {
        moduleId: section.id,
        label,
        description: openResponse ? `${label} \xB7 \u5355\u4EFB\u52A1\u5F00\u653E\u4F5C\u7B54\uFF08${trainingPreset.questionCount} \u9898 \xB7 ${trainingPreset.durationMinutes} \u5206\u949F\uFF09` : examCode === "tcf" ? getTcfModuleDescription(section.id) : `${label} \xB7 \u4E13\u9879\u8BAD\u7EC3\uFF08\u968F\u65F6\u5F00\u7EC3\uFF0C\u968F\u65F6\u53EF\u505C\uFF09`,
        durationMinutes: trainingPreset.durationMinutes,
        questionCount: trainingPreset.questionCount,
        recommended: section.id === selectedModuleId,
        status,
        disabled,
        disabledReason: runtimeBlockReason ? runtimeBlockReason : contentDisabled ? `${label}\u6682\u65E0\u53EF\u5B66\u5185\u5BB9\u3002` : null
      };
    })()
  }));
}
function examDeclaresOfficialMockCounts(examCode) {
  const sections = getExamSections(examCode).filter(
    (section) => section.includeInFullMock !== false
  );
  return sections.length > 0 && sections.every(
    (section) => typeof section.questionCount === "number" && section.questionCount > 0
  );
}
function canStartFullMockExam({
  evidence,
  examCode,
  contentReadiness
}) {
  return hasEnoughDiagnosisEvidence(evidence) && examDeclaresOfficialMockCounts(examCode) && contentReadiness.status === "ready";
}
function resolveMockModuleQuestionTypeId(section) {
  return section.questionTypeConstraints?.[0]?.questionTypeId ?? section.questionTypes?.[0] ?? section.id;
}
function buildFullMockModules(examCode) {
  return getExamSections(examCode).filter((section) => section.includeInFullMock !== false).map((section) => ({
    moduleId: section.id,
    label: getLearningModuleLabel(examCode, section.id),
    durationMinutes: section.duration,
    questionCount: section.questionCount ?? 0,
    questionTypeId: resolveMockModuleQuestionTypeId(section)
  }));
}
function getMockExamStructureMetrics(examCode) {
  const modules = buildFullMockModules(examCode);
  const totalMinutes = modules.reduce((sum, module) => sum + module.durationMinutes, 0);
  const totalQuestions = modules.reduce((sum, module) => sum + module.questionCount, 0);
  const hasOfficialCounts = examDeclaresOfficialMockCounts(examCode);
  return [
    {
      label: hasOfficialCounts ? "\u5168\u771F\u6A21\u8003" : "\u6A21\u8003\u7ED3\u6784",
      value: `${totalMinutes}m`,
      tone: hasOfficialCounts ? "current" : "warning",
      caption: hasOfficialCounts ? `${totalQuestions} \u9898` : `${modules.length} \u79D1 \xB7 \u9898\u91CF\u672A\u914D\u7F6E`
    }
  ];
}
function getTcfModuleDescription(moduleId) {
  const descriptions = {
    listening: "\u6570\u5B57\u3001\u65F6\u95F4\u3001\u5730\u70B9\u4E0E\u610F\u56FE\u8BC6\u522B",
    language_structures: "\u8BED\u6CD5\u7ED3\u6784\u3001\u8FDE\u63A5\u8BCD\u4E0E\u53E5\u5F0F\u5224\u65AD",
    reading: "\u77ED\u6587\u4E3B\u65E8\u3001\u7EC6\u8282\u5B9A\u4F4D\u4E0E\u63A8\u65AD",
    writing: "\u77ED\u6D88\u606F\u3001\u53D9\u8FF0\u4E0E\u8BAE\u8BBA\u6587\u5F0F\u4E66\u9762\u8868\u8FBE"
  };
  return descriptions[moduleId] ?? "\u6309\u8003\u8BD5 section \u7EC4\u7EC7\u4E13\u9879\u8BAD\u7EC3";
}
function getMockExamStateConfig(status, evidence, contentReadiness, activeScope, examCode) {
  const hasOfficialMockCounts = examDeclaresOfficialMockCounts(examCode);
  const canStartFullMock = canStartFullMockExam({
    evidence,
    examCode,
    contentReadiness
  });
  const trainingHref = buildScopedLearningHref("/training", activeScope);
  const diagnosisHref = buildScopedLearningHref("/diagnosis/ability", activeScope);
  const examHref = buildAttemptHref({
    kind: "mock",
    languageId: activeScope?.languageId,
    examType: examCode
  });
  const missingSectionLabels = contentReadiness.missingSectionLabels;
  const hasPartialFullExamCoverage = contentReadiness.learnableBankCount > 0 && missingSectionLabels.length > 0;
  const examLabel = activeScope?.examName ?? examCode.toUpperCase();
  const structureMetrics = getMockExamStructureMetrics(examCode);
  const structureGateReason = hasOfficialMockCounts ? null : {
    code: "MOCK_STRUCTURE_UNCONFIGURED",
    label: "\u5168\u771F\u9898\u91CF\u914D\u7F6E",
    currentValue: `${getExamSections(examCode).filter((section) => section.questionCount).length}/${getExamSections(examCode).length} \u79D1`,
    requiredValue: "\u6BCF\u4E2A Skill \u58F0\u660E questionCount",
    blocking: true
  };
  const baseAvailability = {
    status: "ready",
    canStartFullMock,
    canScheduleStageMock: canStartFullMock,
    gateReasons: structureGateReason ? [structureGateReason] : []
  };
  if (contentReadiness.status !== "ready") {
    return {
      pageTitle: contentReadiness.title,
      pageSubtitle: contentReadiness.description,
      primaryAction: contentReadiness.primaryAction,
      secondaryAction: {
        id: "back-training-from-content-gate",
        label: "\u8FD4\u56DE\u4E13\u9879\u8BAD\u7EC3",
        kind: "secondary",
        href: trainingHref
      },
      availability: {
        ...baseAvailability,
        status: "locked",
        canStartFullMock: false,
        canScheduleStageMock: false,
        gateReasons: [
          {
            code: "LEARNABLE_CONTENT_UNAVAILABLE",
            label: hasPartialFullExamCoverage ? "\u5168\u5957\u6A21\u8003\u5185\u5BB9" : "\u53EF\u5B66\u5185\u5BB9",
            currentValue: contentReadiness.status === "checking" ? "\u786E\u8BA4\u4E2D" : hasPartialFullExamCoverage ? `\u7F3A ${missingSectionLabels.join("\u3001")}` : `${contentReadiness.learnableBankCount} \u9879\u53EF\u5B66\u5185\u5BB9`,
            requiredValue: hasPartialFullExamCoverage ? `\u8986\u76D6 ${contentReadiness.sectionReadiness.length} \u4E2A\u8003\u8BD5\u6A21\u5757` : "\u81F3\u5C11 1 \u4E2A",
            blocking: true
          }
        ]
      },
      metrics: [
        {
          label: "\u53EF\u5B66\u5185\u5BB9",
          value: contentReadiness.status === "checking" ? "\u68C0\u67E5\u4E2D" : `${contentReadiness.learnableBankCount} \u4E2A`,
          tone: contentReadiness.status === "checking" ? "warning" : "locked"
        },
        {
          label: "\u8BAD\u7EC3\u8BC1\u636E",
          value: `${evidence.recordCount} \u7EC4`,
          tone: evidence.recordCount > 0 ? "neutral" : "locked",
          caption: `${evidence.answeredCount} \u9898`
        },
        {
          label: "\u8003\u8BD5\u7A7A\u95F4",
          value: contentReadiness.scopeLabel,
          tone: contentReadiness.tone
        }
      ],
      stateCard: {
        badge: contentReadiness.status === "checking" ? "\u786E\u8BA4\u4E2D" : "\u5185\u5BB9\u672A\u5C31\u7EEA",
        title: contentReadiness.title,
        body: contentReadiness.description,
        tone: contentReadiness.tone
      }
    };
  }
  if (status === "unavailable") {
    return {
      pageTitle: "\u8FD8\u4E0D\u80FD\u5F00\u59CB\u5168\u771F\u6A21\u62DF",
      pageSubtitle: "\u5148\u5B8C\u6210\u63A8\u8350\u4E13\u9879\u8BAD\u7EC3\uFF0C\u7CFB\u7EDF\u4F1A\u5728\u6761\u4EF6\u6EE1\u8DB3\u540E\u5F00\u653E\u9636\u6BB5\u6A21\u8003\u3002",
      primaryAction: {
        id: "go-training",
        label: "\u5148\u5B8C\u6210\u4E13\u9879\u8BAD\u7EC3",
        kind: "primary",
        href: trainingHref
      },
      secondaryAction: {
        id: "view-diagnosis",
        label: "\u67E5\u770B\u80FD\u529B\u8BCA\u65AD",
        kind: "secondary",
        href: diagnosisHref
      },
      availability: {
        ...baseAvailability,
        status: "unavailable",
        canStartFullMock: false,
        gateReasons: [
          {
            code: "MIN_TRAINING_SESSIONS_NOT_MET",
            label: "\u63A8\u8350\u4E13\u9879\u5B8C\u6210",
            currentValue: evidence.recordCount,
            requiredValue: MIN_DIAGNOSIS_TRAINING_SESSIONS,
            blocking: true
          },
          {
            code: "MIN_ANSWERED_QUESTIONS_NOT_MET",
            label: "\u6709\u6548\u4F5C\u7B54\u9898\u6570",
            currentValue: evidence.answeredCount,
            requiredValue: MIN_DIAGNOSIS_ANSWERED_QUESTIONS,
            blocking: true
          }
        ]
      },
      metrics: [
        {
          label: "\u63A8\u8350\u4E13\u9879\u5B8C\u6210",
          value: `${evidence.recordCount}/${MIN_DIAGNOSIS_TRAINING_SESSIONS}`,
          tone: "warning"
        },
        {
          label: "\u6709\u6548\u4F5C\u7B54",
          value: `${evidence.answeredCount}/${MIN_DIAGNOSIS_ANSWERED_QUESTIONS}`,
          tone: "warning"
        },
        { label: "\u8003\u8BD5\u7A7A\u95F4", value: "\u6709\u6548", tone: "success" }
      ],
      stateCard: {
        badge: "\u672A\u5F00\u653E",
        title: "\u5F00\u653E\u6761\u4EF6\u672A\u6EE1\u8DB3",
        body: "\u5F53\u524D\u8BAD\u7EC3\u8BC1\u636E\u8FD8\u4E0D\u8DB3\u4EE5\u652F\u6301\u6574\u5957\u6A21\u62DF\u3002\u5148\u8865\u9F50\u63A8\u8350\u4E13\u9879\uFF0C\u907F\u514D\u8FC7\u65E9\u8FDB\u5165\u4F4E\u8D28\u91CF\u6A21\u8003\u3002",
        tone: "warning"
      }
    };
  }
  if (status === "noRecord") {
    return {
      pageTitle: "\u8FD8\u6CA1\u6709\u8BAD\u7EC3\u8BB0\u5F55",
      pageSubtitle: "\u5148\u5B8C\u6210\u4E13\u9879\u8BAD\u7EC3\u5EFA\u7ACB\u80FD\u529B\u8BC1\u636E\uFF0C\u518D\u8FDB\u5165\u6A21\u62DF\u8003\u8BD5\u3002",
      primaryAction: {
        id: "start-first-training-before-mock",
        label: "\u5F00\u59CB\u4E13\u9879\u8BAD\u7EC3",
        kind: "primary",
        href: trainingHref
      },
      secondaryAction: {
        id: "view-diagnosis",
        label: "\u67E5\u770B\u80FD\u529B\u8BCA\u65AD",
        kind: "secondary",
        href: diagnosisHref
      },
      availability: {
        ...baseAvailability,
        status: "unavailable",
        canStartFullMock: false,
        canScheduleStageMock: false
      },
      metrics: [
        { label: "\u8BAD\u7EC3\u8BB0\u5F55", value: "0 \u7EC4", tone: "neutral" },
        { label: "\u6709\u6548\u4F5C\u7B54", value: "0 \u9898", tone: "neutral" },
        { label: "\u5F53\u524D\u72B6\u6001", value: "\u7B49\u5F85\u8BAD\u7EC3", tone: "locked" }
      ],
      stateCard: {
        badge: "\u672A\u5EFA\u7ACB",
        title: "\u8FD8\u6CA1\u6709\u8DB3\u591F\u8BC1\u636E\u8FDB\u5165\u6A21\u8003",
        body: "\u5F53\u524D\u6CA1\u6709\u4EFB\u4F55\u8BAD\u7EC3\u8BB0\u5F55\u3002\u5148\u5B8C\u6210\u4E13\u9879\u8BAD\u7EC3\uFF0C\u7CFB\u7EDF\u518D\u5224\u65AD\u662F\u5426\u9002\u5408\u5F00\u59CB\u6A21\u62DF\u8003\u8BD5\u3002",
        tone: "locked"
      }
    };
  }
  if (!hasOfficialMockCounts) {
    const configuredCount = getExamSections(examCode).filter(
      (section) => typeof section.questionCount === "number" && section.questionCount > 0
    ).length;
    const sectionCount = getExamSections(examCode).length;
    return {
      pageTitle: `${examLabel} \u6A21\u62DF\u8003\u8BD5`,
      pageSubtitle: "\u8BE5\u8003\u8BD5\u5C1A\u672A\u5728 exams \u5305\u58F0\u660E\u5168\u771F\u9898\u91CF\uFF0C\u4E0D\u80FD\u5F00\u653E\u5168\u771F\u6A21\u8003\u3002",
      primaryAction: {
        id: "back-training-structure-gate",
        label: "\u8FD4\u56DE\u4E13\u9879\u8BAD\u7EC3",
        kind: "primary",
        href: trainingHref
      },
      secondaryAction: {
        id: "view-diagnosis",
        label: "\u67E5\u770B\u80FD\u529B\u8BCA\u65AD",
        kind: "secondary",
        href: diagnosisHref
      },
      availability: {
        ...baseAvailability,
        status: "unavailable",
        canStartFullMock: false,
        canScheduleStageMock: false,
        gateReasons: structureGateReason ? [structureGateReason] : []
      },
      metrics: [
        ...structureMetrics,
        {
          label: "\u9898\u91CF\u914D\u7F6E",
          value: `${configuredCount}/${sectionCount}`,
          tone: "warning",
          caption: "\u9700\u6BCF\u4E2A Skill \u58F0\u660E questionCount"
        },
        {
          label: "\u8BAD\u7EC3\u8BC1\u636E",
          value: `${evidence.recordCount} \u7EC4`,
          tone: evidence.recordCount > 0 ? "neutral" : "locked"
        }
      ],
      stateCard: {
        badge: "\u7ED3\u6784\u672A\u5C31\u7EEA",
        title: "\u5168\u771F\u9898\u91CF\u5C1A\u672A\u914D\u7F6E",
        body: "\u4E13\u9879\u8BAD\u7EC3\u4ECD\u53EF\u6309 Skill \u8FDB\u884C\u3002\u5168\u771F\u6A21\u8003\u8981\u7B49 exams \u5305\u4E3A\u6BCF\u4E2A section \u5199\u5165\u5B98\u65B9 questionCount \u540E\u518D\u5F00\u653E\u3002",
        tone: "warning"
      }
    };
  }
  return {
    pageTitle: `${examLabel} \u6A21\u62DF\u8003\u8BD5`,
    pageSubtitle: "\u6A21\u62DF\u8003\u8BD5\u5165\u53E3\u57FA\u4E8E\u5DF2\u4FDD\u5B58\u8BAD\u7EC3\u8BB0\u5F55\u5F00\u653E\uFF1B\u5F00\u59CB\u540E\u8FDB\u5165\u771F\u5B9E\u8003\u8BD5\u4F5C\u7B54\u6D41\u7A0B\u3002",
    primaryAction: {
      id: "start-full-mock",
      label: "\u5F00\u59CB\u6A21\u62DF\u8003\u8BD5",
      kind: "primary",
      href: examHref
    },
    secondaryAction: {
      id: "continue-training",
      label: "\u7EE7\u7EED\u4E13\u9879\u8BAD\u7EC3",
      kind: "secondary",
      href: trainingHref
    },
    availability: baseAvailability,
    metrics: [
      ...structureMetrics,
      {
        label: "\u8BAD\u7EC3\u8BC1\u636E",
        value: `${evidence.recordCount} \u7EC4`,
        tone: "success",
        caption: `${evidence.answeredCount} \u9898`
      },
      {
        label: "\u6700\u8FD1\u8BB0\u5F55",
        value: evidence.evidenceLabel,
        tone: "neutral"
      }
    ],
    stateCard: {
      badge: "\u53EF\u5F00\u59CB",
      title: "\u8BAD\u7EC3\u8BC1\u636E\u5DF2\u6EE1\u8DB3\u6A21\u8003\u5165\u53E3",
      body: "\u5B8C\u6210\u6A21\u8003\u540E\u4E0D\u8FDB\u5165\u72EC\u7ACB\u590D\u76D8\u4E2D\u5FC3\uFF0C\u8868\u73B0\u4F1A\u5199\u5165\u80FD\u529B\u8BCA\u65AD\uFF0C\u5E76\u751F\u6210\u4E0B\u4E00\u6B65\u8BAD\u7EC3\u5904\u65B9\u3002",
      tone: "current"
    }
  };
}

// src/recommendations/learningAdvisorModel.ts
function analyzeLearningWeakAreas(records, examCode = "tcf") {
  const moduleStats = /* @__PURE__ */ new Map();
  for (const record of records) {
    const moduleId = normalizeAdvisorModuleId(record.moduleId);
    const existing = moduleStats.get(moduleId) ?? {
      moduleId,
      label: getLearningModuleLabel(examCode, moduleId),
      correct: 0,
      total: 0,
      mistakes: 0,
      sessions: []
    };
    existing.correct += record.correctCount ?? 0;
    existing.total += record.answeredCount ?? 0;
    existing.mistakes += countClosedChoiceWrongMetrics(record.questionMetrics);
    existing.sessions.push(record.completedAt);
    moduleStats.set(moduleId, existing);
  }
  return Array.from(moduleStats.values()).filter((stats) => stats.total > 0).map((stats) => ({
    moduleId: stats.moduleId,
    moduleLabel: stats.label,
    accuracy: Math.round(stats.correct / stats.total * 100),
    totalQuestions: stats.total,
    mistakeCount: stats.mistakes,
    trend: resolveAccuracyTrend(stats.sessions)
  })).sort((left, right) => left.accuracy - right.accuracy);
}
function buildLearningRecommendationsFromWeakAreas(weakAreas, examCode = "tcf") {
  return weakAreas.slice(0, 3).map((area, index) => ({
    id: `weak-area-${area.moduleId}-${index}`,
    title: `${area.moduleLabel}\u8584\u5F31\u70B9\u7A81\u7834`,
    description: `\u57FA\u4E8E\u6700\u8FD1\u8BAD\u7EC3\u6570\u636E\uFF0C\u4F60\u5728 ${area.moduleLabel} \u7684\u6B63\u786E\u7387\u4EC5\u4E3A ${area.accuracy}%\u3002\u5EFA\u8BAE\u4F18\u5148\u8FDB\u884C\u9488\u5BF9\u6027\u8BAD\u7EC3\u3002`,
    targetModule: {
      id: area.moduleId,
      label: area.moduleLabel
    },
    confidence: area.accuracy < 50 ? "high" : area.accuracy < 70 ? "medium" : "low",
    reason: `${area.mistakeCount} \u9053\u9519\u9898 \xB7 ${area.totalQuestions} \u9898\u6837\u672C`,
    actionHref: buildTrainingEntryHref({
      languageId: buildPlatformScope9({ examType: examCode })?.languageId ?? "french",
      examType: examCode,
      skillId: area.moduleId,
      intensityHint: "standard"
    }),
    priority: index + 1
  }));
}
function mapAiLearningSuggestions(aiSuggestions, weakAreas, examCode = "tcf") {
  if (!aiSuggestions || aiSuggestions.length === 0) {
    return buildLearningRecommendationsFromWeakAreas(weakAreas, examCode);
  }
  return aiSuggestions.map((suggestion, index) => {
    const moduleId = normalizeAdvisorModuleId(
      suggestion.targetModule ?? suggestion.module ?? "listening"
    );
    const moduleLabel = getLearningModuleLabel(examCode, moduleId);
    return {
      id: suggestion.id ?? `ai-rec-${index}`,
      title: suggestion.title ?? suggestion.recommendation ?? `${moduleLabel}\u8BAD\u7EC3\u5EFA\u8BAE`,
      description: suggestion.description ?? suggestion.reason ?? "",
      targetModule: {
        id: moduleId,
        label: moduleLabel
      },
      confidence: suggestion.confidence ?? "medium",
      reason: suggestion.reason ?? suggestion.basedOn ?? "AI\u667A\u80FD\u5206\u6790",
      actionHref: suggestion.actionHref ?? buildTrainingEntryHref({
        languageId: buildPlatformScope9({ examType: examCode })?.languageId ?? "french",
        examType: examCode,
        skillId: moduleId,
        intensityHint: "standard"
      }),
      priority: suggestion.priority ?? index + 1
    };
  });
}
function buildLearningAdvisorResult({
  records,
  examCode = "tcf",
  aiSuggestions,
  now = Date.now()
}) {
  const weakAreas = analyzeLearningWeakAreas(records, examCode);
  return {
    weakAreas,
    recommendations: mapAiLearningSuggestions(aiSuggestions, weakAreas, examCode),
    analysisTimestamp: now
  };
}
function normalizeAdvisorModuleId(moduleId) {
  const normalized = String(moduleId ?? "").trim().toLowerCase();
  return normalized || "listening";
}
function resolveAccuracyTrend(timestamps) {
  if (timestamps.length < 2) return "stable";
  return "stable";
}

// src/models/publicLanding.ts
import { buildPlatformScope as buildPlatformScope10 } from "@examflow/core";
import { getExamConfig as getExamConfig6 } from "@examflow/exams";
function isExamCode(value) {
  return getExamSelectionCatalog().includes(value);
}
function buildExamPublicLandingModel(examId) {
  if (!isExamCode(examId)) return null;
  const config = getExamConfig6(examId);
  if (!config) return null;
  const scope = buildPlatformScope10({ examType: examId });
  if (!scope) return null;
  const redirect = `/workspace?languageId=${encodeURIComponent(scope.languageId)}&examType=${encodeURIComponent(examId)}`;
  const registerHref = `/register?redirect=${encodeURIComponent(redirect)}`;
  const skills = getExamSections(examId).map((section) => ({
    id: section.id,
    name: section.name,
    durationMinutes: section.duration ?? 0,
    officialMockQuestionCount: section.questionCount ?? 0,
    includeInFullMock: section.includeInFullMock !== false
  }));
  return {
    examId,
    examName: scope.examName,
    languageId: scope.languageId,
    languageName: scope.languageName,
    description: config.description,
    eyebrow: `${scope.languageName} \xB7 \u8003\u8BD5\u7A7A\u95F4`,
    title: scope.examName,
    titleAccent: "\u8003\u8BD5\u7A7A\u95F4",
    structureDisclaimer: "\u4E0B\u8868\u4E3A\u5B98\u65B9 Mock \u7ED3\u6784\uFF08\u65F6\u957F\u4E0E\u9898\u91CF\uFF09\u3002\u65E5\u5E38\u4E13\u9879\u8BAD\u7EC3\u9898\u91CF\u7531\u8BAD\u7EC3\u5F3A\u5EA6\u51B3\u5B9A\uFF0C\u4E0D\u4EE5\u5B98\u65B9\u6A21\u8003\u9898\u91CF\u4E3A\u65E5\u5E38\u7EC3\u4E60\u91CF\u3002",
    skills,
    primaryAction: {
      id: `register-${examId}-space`,
      label: "\u8FDB\u5165\u8003\u8BD5\u7A7A\u95F4",
      kind: "primary",
      href: registerHref
    },
    secondaryAction: {
      id: "back-exam-spaces",
      label: "\u6D4F\u89C8\u5168\u90E8\u8003\u8BD5",
      kind: "secondary",
      href: "/#exam-spaces"
    }
  };
}
function listPublicMarketingPaths() {
  return ["/", ...getExamSelectionCatalog().map((id) => `/exams/${id}`)];
}

// src/models/writingFocus.ts
function buildWritingFocusPrescription(examCode, contentReadiness, trainingRecords = [], nowMs = Date.now()) {
  if (!examHasWritingSkill(examCode)) return null;
  if (contentReadiness.status !== "ready") return null;
  const writingSection = contentReadiness.sectionReadiness.find(
    (section) => section.sectionId === "writing"
  );
  if (!writingSection || writingSection.status !== "ready") return null;
  const evidence = summarizeWritingEvidence(trainingRecords, examCode, nowMs);
  return buildExecutableTrainingPrescription({
    contentPack: examCode,
    moduleId: "writing",
    intensity: "standard",
    reason: evidence.focusReason,
    taskNumber: evidence.recommendedTaskNumber ?? void 0
  });
}

// src/models/workspace.ts
import { buildPlatformScope as buildPlatformScope11 } from "@examflow/core";

// src/models/examWorkspaceProfile.ts
import { getExamConfig as getExamConfig7 } from "@examflow/exams";
var DEFAULT_COPY = {
  skillMapTitle: "\u8003\u8BD5\u7ED3\u6784",
  skillMapSubtitle: "\u6309 Skill \u8FDB\u5165\u4E13\u9879\u8BAD\u7EC3",
  emptyDiagnosisDescription: "\u8FD8\u6CA1\u6709\u80FD\u529B\u57FA\u7EBF\u3002\u5B8C\u6210\u4E00\u6B21\u8BCA\u65AD\u8BAD\u7EC3\u540E\uFF0C\u5DE5\u4F5C\u53F0\u624D\u4F1A\u7ED9\u51FA\u7A33\u5B9A\u7684\u4E0B\u4E00\u6B65\u5904\u65B9\u3002",
  mockReadyDescription: "\u53EF\u4EE5\u8FDB\u5165\u771F\u5B9E\u8003\u8BD5\u4F5C\u7B54\u5165\u53E3\uFF0C\u63D0\u4EA4\u540E\u518D\u66F4\u65B0\u80FD\u529B\u8BCA\u65AD\u3002",
  mockPendingDescription: "\u5148\u8865\u9F50\u4E13\u9879\u8BAD\u7EC3\u8BC1\u636E\uFF0C\u518D\u8FDB\u5165\u9636\u6BB5\u6A21\u8003\u3002",
  structureCue: "\u6309\u5F53\u524D\u8003\u8BD5\u7ED3\u6784\u7EC4\u7EC7\u8BAD\u7EC3\u4E0E\u6A21\u8003"
};
var COPY_OVERRIDES = {
  tcf: {
    skillMapTitle: "TCF \u8003\u8BD5\u7ED3\u6784",
    skillMapSubtitle: "\u542C\u529B \xB7 \u8BED\u8A00\u7ED3\u6784 \xB7 \u9605\u8BFB \xB7 \u53EF\u9009\u5199\u4F5C",
    emptyDiagnosisDescription: "\u8FD8\u6CA1\u6709 TCF \u80FD\u529B\u57FA\u7EBF\u3002\u5148\u5B8C\u6210\u5165\u95E8\u8BCA\u65AD\uFF0C\u518D\u6309\u542C\u529B / \u8BED\u8A00\u7ED3\u6784 / \u9605\u8BFB\u8865\u5F3A\uFF1B\u5199\u4F5C\u53EF\u5355\u72EC\u7EC3\u4E60\u3002",
    mockReadyDescription: "TCF \u5168\u771F\u6A21\u8003\u6309\u5B98\u65B9\u542C\u529B\u3001\u8BED\u8A00\u7ED3\u6784\u3001\u9605\u8BFB\u9898\u91CF\u7EC4\u7EC7\uFF1B\u4EA4\u5377\u540E\u5199\u5165\u80FD\u529B\u8BCA\u65AD\u3002\u4E66\u9762\u8868\u8FBE\u4E3A\u53EF\u9009\u4E13\u9879\uFF0C\u4E0D\u8FDB\u672C\u5F3A\u5236\u5377\u3002",
    mockPendingDescription: "\u8FDB\u5165 TCF \u5168\u771F\u6A21\u8003\u524D\uFF0C\u5148\u79EF\u7D2F\u8DB3\u591F\u7684\u4E13\u9879\u8BAD\u7EC3\u8BC1\u636E\uFF08\u542C / \u7ED3\u6784 / \u8BFB\uFF09\u3002",
    structureCue: "\u542C\u529B \xB7 \u8BED\u8A00\u7ED3\u6784 \xB7 \u9605\u8BFB \xB7 \u53EF\u9009\u5199\u4F5C"
  },
  ielts: {
    skillMapTitle: "IELTS \u56DB\u79D1",
    skillMapSubtitle: "\u542C \xB7 \u8BFB \xB7 \u5199 \xB7 \u8BF4",
    emptyDiagnosisDescription: "\u8FD8\u6CA1\u6709 IELTS \u80FD\u529B\u57FA\u7EBF\u3002\u5148\u5B8C\u6210\u5165\u95E8\u8BCA\u65AD\uFF0C\u518D\u6309\u542C\u8BF4\u8BFB\u5199\u56DB\u79D1\u63A8\u8FDB\u3002",
    mockReadyDescription: "IELTS \u6A21\u8003\u6309\u542C / \u8BFB\u5B98\u65B9\u9898\u91CF\u7EC4\u7EC7\uFF1B\u5199\u4F5C\u53EF\u8D70\u4E13\u9879\u5F00\u653E\u4F5C\u7B54\uFF0C\u53E3\u8BED Runtime \u5C1A\u672A\u63A5\u5165\u3002",
    mockPendingDescription: "\u8FDB\u5165 IELTS \u9636\u6BB5\u6A21\u8003\u524D\uFF0C\u5148\u5728\u542C / \u8BFB / \u5199\u4E13\u9879\u4E0A\u79EF\u7D2F\u8DB3\u591F\u8BAD\u7EC3\u8BC1\u636E\u3002",
    structureCue: "\u542C \xB7 \u8BFB \xB7 \u5199 \xB7 \u8BF4"
  },
  jlpt: {
    skillMapTitle: "JLPT \u8003\u8BD5\u7ED3\u6784",
    skillMapSubtitle: "\u8BCD\u6C47 \xB7 \u6587\u6CD5\u8BFB\u89E3 \xB7 \u542C\u529B",
    structureCue: "\u8BCD\u6C47 \xB7 \u6587\u6CD5\u8BFB\u89E3 \xB7 \u542C\u529B"
  },
  topik: {
    skillMapTitle: "TOPIK \u8003\u8BD5\u7ED3\u6784",
    skillMapSubtitle: "\u542C \xB7 \u8BFB \xB7 \u5199",
    structureCue: "\u542C \xB7 \u8BFB \xB7 \u5199"
  },
  dele: {
    skillMapTitle: "DELE \u7B14\u8BD5\u7ED3\u6784",
    skillMapSubtitle: "\u8BFB \xB7 \u542C \xB7 \u5199\uFF08\u53E3\u8BD5\u5355\u72EC\u5B89\u6392\uFF09",
    structureCue: "\u8BFB \xB7 \u542C \xB7 \u5199",
    mockReadyDescription: "DELE \u5DE5\u4F5C\u53F0\u6A21\u8003\u8986\u76D6\u7B14\u8BD5\u7ED3\u6784\uFF1B\u53E3\u8BD5\u4E0D\u5728\u672C\u5F3A\u5236\u5377\u5185\uFF0C\u8BF7\u6309\u8003\u8BD5\u5B89\u6392\u5355\u72EC\u51C6\u5907\u3002"
  },
  toefl: {
    skillMapTitle: "TOEFL \u56DB\u79D1",
    skillMapSubtitle: "\u8BFB \xB7 \u542C \xB7 \u8BF4 \xB7 \u5199",
    structureCue: "\u8BFB \xB7 \u542C \xB7 \u8BF4 \xB7 \u5199"
  },
  siele: {
    skillMapTitle: "SIELE \u56DB\u79D1",
    skillMapSubtitle: "\u8BFB \xB7 \u542C \xB7 \u5199 \xB7 \u8BF4",
    structureCue: "\u8BFB \xB7 \u542C \xB7 \u5199 \xB7 \u8BF4"
  }
};
function resolveStructureShape(examCode, sectionIds) {
  const ids = new Set(sectionIds);
  if (ids.has("language_structures")) return "triple_tcf";
  if (ids.has("vocabulary") || ids.has("grammar_reading")) return "jlpt_triple";
  if (ids.has("listening") && ids.has("reading") && ids.has("writing") && !ids.has("speaking")) {
    return examCode === "dele" ? "dele_no_speaking" : "topik_triple";
  }
  if (ids.has("listening") && ids.has("reading") && ids.has("writing") && ids.has("speaking")) {
    return "four_skills";
  }
  return "generic";
}
function resolveMockCredibility(examCode) {
  const sections = getExamSections(examCode);
  if (sections.length === 0) return "unconfigured";
  if (examDeclaresOfficialMockCounts(examCode)) return "official_counts";
  const anyCount = sections.some(
    (section) => typeof section.questionCount === "number" && section.questionCount > 0
  );
  return anyCount ? "reference_only" : "unconfigured";
}
function resolveHighlightSkillIds(structureShape, sectionIds) {
  const ids = new Set(sectionIds);
  switch (structureShape) {
    case "triple_tcf":
      return ids.has("language_structures") ? ["language_structures"] : [];
    case "jlpt_triple":
      return ids.has("vocabulary") ? ["vocabulary"] : [];
    case "four_skills":
      return ids.has("speaking") ? ["speaking"] : [];
    case "dele_no_speaking":
    case "topik_triple":
      return ids.has("writing") ? ["writing"] : [];
    case "generic":
      return [];
    default: {
      const _exhaustive = structureShape;
      void _exhaustive;
      return [];
    }
  }
}
function resolveAudioSkillIds(examCode, sectionIds) {
  const config = getExamConfig7(examCode);
  const definitions = Object.values(config?.questionTypeDefinitions ?? {});
  const audioTypeIds = new Set(
    definitions.filter((definition) => definition.requiredStimulus?.includes("audio")).map((definition) => definition.id)
  );
  return getExamSections(examCode).filter((section) => {
    if (!sectionIds.includes(section.id)) return false;
    if (section.id === "listening" || section.id.endsWith("_listening")) return true;
    const typeIds = [
      ...section.questionTypeConstraints?.map((item) => item.questionTypeId) ?? [],
      ...section.questionTypes ?? []
    ];
    return typeIds.some((typeId) => audioTypeIds.has(typeId));
  }).map((section) => section.id);
}
function resolveOpenResponseSkillIds(sectionIds) {
  return sectionIds.filter((id) => id === "speaking" || id === "writing" || id.endsWith("_speaking"));
}
function buildCopy(examCode) {
  return {
    ...DEFAULT_COPY,
    ...COPY_OVERRIDES[examCode]
  };
}
function buildExamWorkspaceProfile(examCode) {
  const sections = getExamSections(examCode);
  const sectionIds = sections.map((section) => section.id);
  const structureShape = resolveStructureShape(examCode, sectionIds);
  const mockCredibility = resolveMockCredibility(examCode);
  const copy = buildCopy(examCode);
  if (mockCredibility === "reference_only" && !COPY_OVERRIDES[examCode]?.mockReadyDescription) {
    copy.mockReadyDescription = "\u5F53\u524D\u8003\u8BD5\u6A21\u8003\u7ED3\u6784\u4E3A\u53C2\u8003\u9898\u91CF\uFF1B\u8FDB\u5165\u540E\u6309\u5DF2\u914D\u7F6E\u6A21\u5757\u7EC4\u7EC7\uFF0C\u4EA4\u5377\u5199\u5165\u80FD\u529B\u8BCA\u65AD\u3002";
  }
  if (mockCredibility === "unconfigured" && !COPY_OVERRIDES[examCode]?.mockPendingDescription) {
    copy.mockPendingDescription = "\u8BE5\u8003\u8BD5\u5C1A\u672A\u914D\u7F6E\u5B8C\u6574\u5B98\u65B9\u6A21\u8003\u9898\u91CF\uFF1B\u5148\u7EE7\u7EED\u4E13\u9879\u8BAD\u7EC3\uFF0C\u6A21\u8003\u5165\u53E3\u4F1A\u5728\u7ED3\u6784\u5C31\u7EEA\u540E\u5F00\u653E\u3002";
  }
  return {
    examCode,
    structureShape,
    mockCredibility,
    highlightSkillIds: resolveHighlightSkillIds(structureShape, sectionIds),
    openResponseSkillIds: resolveOpenResponseSkillIds(sectionIds),
    openResponseRuntimeReadySkillIds: resolveOpenResponseSkillIds(sectionIds).filter(
      (skillId) => isOpenResponseRuntimeReadySkillId(skillId)
    ),
    audioSkillIds: resolveAudioSkillIds(examCode, sectionIds),
    copy
  };
}

// src/models/workspace.ts
function buildSkillRailShortLabel(moduleId, fullLabel) {
  const known = {
    listening: "\u542C",
    reading: "\u8BFB",
    writing: "\u5199",
    speaking: "\u8BF4",
    language_structures: "\u7ED3\u6784",
    vocabulary: "\u8BCD\u6C47",
    grammar_reading: "\u6587\u6CD5"
  };
  if (known[moduleId]) return known[moduleId];
  const withoutParen = fullLabel.replace(/\s*\([^)]*\)\s*/g, "").trim();
  return withoutParen.slice(0, 2) || moduleId;
}
function buildWorkspaceSkillRails(examCode, sectionReadiness, activeScope) {
  const readinessBySection = new Map(
    sectionReadiness.map((section) => [section.sectionId, section])
  );
  const languageId = activeScope?.languageId;
  if (!languageId) return [];
  return getExamSections(examCode).map((section) => {
    const readiness = readinessBySection.get(section.id);
    const label = readiness?.label ?? getLearningModuleLabel(examCode, section.id);
    const runtimeBlockReason = resolveOpenResponseRuntimeBlockReason(section.id);
    return {
      moduleId: section.id,
      label,
      shortLabel: buildSkillRailShortLabel(section.id, label),
      status: readiness?.status ?? "contentUnavailable",
      learnableBankCount: readiness?.learnableBankCount ?? 0,
      href: buildTrainingEntryHref({
        languageId,
        examType: examCode,
        skillId: section.id
      }),
      runtimeLaunchable: runtimeBlockReason == null,
      runtimeBlockReason
    };
  });
}
function buildWorkspaceRemediationSummary(examCode, trainingHref, diagnosisHref, latestRecord) {
  if (!latestRecord) {
    return {
      status: "empty",
      title: "\u7B49\u5F85\u9996\u6B21\u8BAD\u7EC3\u7ED3\u679C",
      description: "\u5B8C\u6210\u4E13\u9879\u8BAD\u7EC3\u540E\uFF0C\u9519\u9898\u4F1A\u8FDB\u5165\u590D\u76D8\u95ED\u73AF\uFF0C\u518D\u56DE\u5230\u80FD\u529B\u5904\u65B9\u3002",
      wrongCount: 0,
      focusModuleId: null,
      primaryAction: {
        id: "start-training-before-review",
        label: "\u5148\u5F00\u59CB\u8BAD\u7EC3",
        kind: "secondary",
        href: trainingHref
      }
    };
  }
  const wrongCount = countClosedChoiceWrongMetrics(latestRecord.questionMetrics);
  const focusModuleId = normalizeTcfModuleId(
    latestRecord.reviewFocus || latestRecord.skillId || latestRecord.moduleId
  );
  const focusLabel = getLearningModuleLabel(examCode, focusModuleId);
  if (isRemediationTrainingRecord(latestRecord)) {
    return {
      status: "clear",
      title: `${focusLabel}\u590D\u76D8\u5DF2\u56DE\u6D41`,
      description: "\u6700\u8FD1\u4E00\u6B21\u9519\u9898\u77ED\u8BAD\u5DF2\u7ECF\u5199\u56DE\u80FD\u529B\u8BCA\u65AD\uFF0C\u4E0B\u4E00\u6B65\u56DE\u5230\u6807\u51C6\u8BAD\u7EC3\u5904\u65B9\u7EE7\u7EED\u8865\u5F3A\u3002",
      wrongCount: 0,
      focusModuleId,
      primaryAction: {
        id: "continue-prescription-after-remediation",
        label: "\u67E5\u770B\u80FD\u529B\u5904\u65B9",
        kind: "secondary",
        href: diagnosisHref
      }
    };
  }
  if (wrongCount <= 0) {
    return {
      status: "clear",
      title: `${focusLabel}\u6682\u65E0\u9519\u9898`,
      description: "\u6700\u8FD1\u4E00\u6B21\u8BAD\u7EC3\u6CA1\u6709\u53EF\u590D\u76D8\u9519\u9898\uFF0C\u53EF\u4EE5\u7EE7\u7EED\u4E0B\u4E00\u8F6E\u63A8\u8350\u8BAD\u7EC3\u6216\u67E5\u770B\u80FD\u529B\u8BCA\u65AD\u3002",
      wrongCount: 0,
      focusModuleId,
      primaryAction: {
        id: "continue-prescription-after-clear-review",
        label: "\u67E5\u770B\u80FD\u529B\u5904\u65B9",
        kind: "secondary",
        href: diagnosisHref
      }
    };
  }
  const activeScope = buildPlatformScope11({ examType: examCode });
  return {
    status: "available",
    title: `${focusLabel}\u9519\u9898\u5F85\u590D\u76D8`,
    description: `\u6700\u8FD1\u4E00\u6B21${focusLabel}\u8BAD\u7EC3\u6709 ${wrongCount} \u9053\u9519\u9898\u3002\u5148\u5728\u7ED3\u679C\u9875\u770B\u89E3\u6790\uFF0C\u518D\u505A\u4E00\u7EC4\u540C\u7C7B\u77ED\u8BAD\u5E76\u66F4\u65B0\u80FD\u529B\u5904\u65B9\u3002`,
    wrongCount,
    focusModuleId,
    primaryAction: {
      id: "start-remediation-short-training",
      label: `\u91CD\u505A${focusLabel}\u77ED\u8BAD`,
      kind: "primary",
      href: buildAttemptHref({
        kind: "remediation",
        languageId: activeScope?.languageId,
        examType: examCode,
        skillId: focusModuleId,
        intensity: "short",
        reviewFocus: focusModuleId,
        next: diagnosisHref
      })
    }
  };
}
function buildWorkspaceNextStep({
  examCode,
  contentReadiness,
  prescription,
  remediationSummary,
  mockExamSummary,
  diagnosisHref,
  needsDiagnosisEntry,
  emptyDiagnosisDescription,
  digestRecentMock
}) {
  if (contentReadiness.status !== "ready") {
    return {
      id: "resolve-content-gap",
      title: contentReadiness.title,
      description: contentReadiness.description,
      tone: contentReadiness.tone,
      action: contentReadiness.primaryAction
    };
  }
  if (needsDiagnosisEntry) {
    return {
      id: "start-ability-diagnosis",
      title: "\u5EFA\u8BAE\u5148\u505A\u5165\u95E8\u8BCA\u65AD",
      description: emptyDiagnosisDescription,
      tone: "current",
      action: {
        id: "open-diagnosis-entry",
        label: "\u5F00\u59CB\u80FD\u529B\u8BCA\u65AD",
        kind: "primary",
        href: diagnosisHref
      }
    };
  }
  if (remediationSummary.status === "available") {
    return {
      id: "remediate-latest-errors",
      title: `\u5148\u590D\u76D8${getLearningModuleLabel(examCode, remediationSummary.focusModuleId ?? "listening")}\u9519\u9898`,
      description: remediationSummary.description,
      tone: "warning",
      action: remediationSummary.primaryAction
    };
  }
  if (digestRecentMock) {
    return {
      id: "digest-mock-snapshot",
      title: `\u5148\u6D88\u5316\u6A21\u8003\u7ED3\u8BBA \xB7 \u8BAD\u7EC3${prescription.moduleLabel}`,
      description: `\u6700\u8FD1\u4E00\u6B21\u6A21\u62DF\u8003\u8BD5\u5DF2\u5199\u5165\u80FD\u529B\u5FEB\u7167\u3002\u5148\u6309\u8584\u5F31\u6A21\u5757\u505A\u4E13\u9879\u8BAD\u7EC3\uFF0C\u518D\u8003\u8651\u4E0B\u4E00\u573A\u6A21\u8003\u3002${prescription.reason}`,
      tone: "current",
      action: prescription.primaryAction
    };
  }
  if (mockExamSummary.status === "available") {
    return {
      id: "start-mock-exam",
      title: "\u8FDB\u5165\u9636\u6BB5\u6A21\u8003",
      description: mockExamSummary.description,
      tone: "success",
      action: mockExamSummary.primaryAction
    };
  }
  return {
    id: "continue-prescription-training",
    title: `\u7EE7\u7EED${prescription.moduleLabel}\u8BAD\u7EC3`,
    description: prescription.reason,
    tone: "current",
    action: prescription.primaryAction
  };
}
function resolveWorkspaceLoopStep(nextStep) {
  switch (nextStep.id) {
    case "resolve-content-gap":
      return "content";
    case "start-ability-diagnosis":
      return "diagnosis";
    case "start-mock-exam":
      return "mockExam";
    case "digest-mock-snapshot":
    case "remediate-latest-errors":
    case "continue-prescription-training":
    case "follow-ability-prescription":
    case "review-result-errors":
    case "continue-result-prescription":
      return "training";
    default: {
      const _exhaustive = nextStep.id;
      void _exhaustive;
      return "training";
    }
  }
}
function buildEvidenceAbilityLine({
  evidence,
  hasMinimumEvidence,
  weakestAccuracy,
  latestSnapshot
}) {
  const weakestSnapshotDimension = getWeakestAbilitySnapshotDimension(latestSnapshot);
  if (latestSnapshot && weakestSnapshotDimension) {
    return {
      displayLevel: latestSnapshot.level ?? String(latestSnapshot.overallScore),
      stabilityLabel: "\u57FA\u4E8E\u80FD\u529B\u5FEB\u7167",
      weakestDimensionLabel: `${weakestSnapshotDimension.label ?? weakestSnapshotDimension.sectionId} \xB7 ${weakestSnapshotDimension.accuracy}%`
    };
  }
  if (evidence.recordCount === 0) {
    return {
      displayLevel: "\u672A\u6D4B",
      stabilityLabel: "\u7B49\u5F85\u9996\u7EC4\u8BAD\u7EC3\u8BB0\u5F55",
      weakestDimensionLabel: "\u5B8C\u6210\u8BAD\u7EC3\u540E\u663E\u793A\u8584\u5F31\u6A21\u5757"
    };
  }
  if (hasMinimumEvidence) {
    return {
      displayLevel: "\u6682\u4F30",
      stabilityLabel: `\u57FA\u4E8E ${evidence.recordCount} \u7EC4\u8BAD\u7EC3\u8BB0\u5F55`,
      weakestDimensionLabel: `${evidence.weakestModuleLabel} \xB7 ${weakestAccuracy}% \u5E73\u5747\u6B63\u786E\u7387`
    };
  }
  return {
    displayLevel: "\u7F3A\u6837\u672C",
    stabilityLabel: `\u6837\u672C\u4E0D\u8DB3 \xB7 ${evidence.answeredCount}/${MIN_DIAGNOSIS_ANSWERED_QUESTIONS} \u9898`,
    weakestDimensionLabel: `${evidence.weakestModuleLabel} \xB7 ${weakestAccuracy}% \u5E73\u5747\u6B63\u786E\u7387`
  };
}
function buildWorkspaceChrome({
  nextStep,
  examCode,
  prescription,
  remediationSummary,
  evidenceAbilityLine
}) {
  switch (nextStep.id) {
    case "resolve-content-gap":
      return {
        highlightedModuleId: null,
        abilityLine: {
          displayLevel: "\u672A\u5C31\u7EEA",
          stabilityLabel: "\u5185\u5BB9\u95E8\u7981",
          weakestDimensionLabel: nextStep.description
        },
        mockBand: "hidden",
        shellStatusKind: "content_gap",
        shellStatusLabel: "\u5185\u5BB9\u672A\u5C31\u7EEA"
      };
    case "start-ability-diagnosis":
      return {
        highlightedModuleId: null,
        abilityLine: {
          displayLevel: "\u672A\u6D4B",
          stabilityLabel: "\u5EFA\u8BAE\u5148\u505A\u5165\u95E8\u8BCA\u65AD",
          weakestDimensionLabel: "\u5EFA\u7ACB\u80FD\u529B\u57FA\u7EBF\u540E\u518D\u8C08\u8584\u5F31\u6A21\u5757"
        },
        mockBand: "hidden",
        shellStatusKind: "diagnosis",
        shellStatusLabel: "\u5165\u95E8\u8BCA\u65AD"
      };
    case "remediate-latest-errors": {
      const focus = remediationSummary.focusModuleId;
      const focusLabel = focus ? getLearningModuleLabel(examCode, focus) : "\u9519\u9898";
      return {
        highlightedModuleId: focus,
        abilityLine: {
          displayLevel: evidenceAbilityLine.displayLevel,
          stabilityLabel: "\u9519\u9898\u4F18\u5148",
          weakestDimensionLabel: `${focusLabel} \xB7 ${remediationSummary.wrongCount} \u9053\u5F85\u590D\u76D8`
        },
        mockBand: "progress",
        shellStatusKind: "remediation",
        shellStatusLabel: "\u9519\u9898\u4F18\u5148"
      };
    }
    case "start-mock-exam":
      return {
        highlightedModuleId: null,
        abilityLine: evidenceAbilityLine,
        mockBand: "confirm",
        shellStatusKind: "mock_ready",
        shellStatusLabel: "\u53EF\u6A21\u8003"
      };
    case "digest-mock-snapshot":
      return {
        highlightedModuleId: prescription.moduleId,
        abilityLine: {
          displayLevel: evidenceAbilityLine.displayLevel,
          stabilityLabel: "\u6A21\u8003\u521A\u7ED3\u675F \xB7 \u57FA\u4E8E\u80FD\u529B\u5FEB\u7167",
          weakestDimensionLabel: evidenceAbilityLine.weakestDimensionLabel
        },
        mockBand: "progress",
        shellStatusKind: "mock_digestion",
        shellStatusLabel: "\u6D88\u5316\u6A21\u8003"
      };
    case "continue-prescription-training":
      return {
        highlightedModuleId: prescription.moduleId,
        abilityLine: evidenceAbilityLine,
        mockBand: "progress",
        shellStatusKind: "prescription",
        shellStatusLabel: "\u7EE7\u7EED\u8BAD\u7EC3"
      };
    case "follow-ability-prescription":
    case "review-result-errors":
    case "continue-result-prescription":
      return {
        highlightedModuleId: prescription.moduleId,
        abilityLine: evidenceAbilityLine,
        mockBand: "progress",
        shellStatusKind: "prescription",
        shellStatusLabel: "\u7EE7\u7EED\u8BAD\u7EC3"
      };
    default: {
      const _exhaustive = nextStep.id;
      void _exhaustive;
      return {
        highlightedModuleId: null,
        abilityLine: evidenceAbilityLine,
        mockBand: "hidden",
        shellStatusKind: "prescription",
        shellStatusLabel: "\u7EE7\u7EED\u8BAD\u7EC3"
      };
    }
  }
}
function buildExamWorkspacePageModel(viewer, trainingRecords = [], readinessInput = {}, options = {}) {
  const examCode = viewer.activeExamSpace.examCode;
  const profile = buildExamWorkspaceProfile(examCode);
  const contentReadiness = buildLearnerContentReadiness(viewer, readinessInput);
  const mockContentReadiness = buildLearnerContentReadiness(viewer, {
    ...readinessInput,
    requireFullExamCoverage: true
  });
  const scopedTrainingRecords = filterTrainingRecordsByExamScope(trainingRecords, examCode);
  const evidence = summarizeDiagnosisTrainingEvidence(scopedTrainingRecords, examCode);
  const hasMinimumEvidence = hasEnoughDiagnosisEvidence(evidence);
  const abilitySnapshots = options.abilitySnapshots ?? [];
  const latestSnapshot = getLatestAbilitySnapshot(abilitySnapshots);
  const prescriptionTarget = resolveTrainingPrescriptionModuleId({
    trainingEvidence: evidence,
    abilitySnapshots
  });
  const mockExamAvailable = canStartFullMockExam({
    evidence,
    examCode,
    contentReadiness: mockContentReadiness
  });
  const latestRecord = scopedTrainingRecords.slice().sort((a, b) => b.completedAt - a.completedAt)[0];
  const weakestModule = evidence.moduleSummaries.find(
    (summary) => summary.moduleId === evidence.weakestModuleId
  );
  const prescription = buildExecutableTrainingPrescription({
    contentPack: examCode,
    moduleId: prescriptionTarget.moduleId,
    reason: prescriptionTarget.reason
  });
  const writingFocus = buildWritingFocusPrescription(
    examCode,
    contentReadiness,
    scopedTrainingRecords
  );
  const activeScope = buildPlatformScope11({ examType: examCode });
  const trainingHref = buildScopedLearningHref("/training", activeScope);
  const diagnosisHref = buildScopedLearningHref("/diagnosis/ability", activeScope);
  const mockExamHref = buildScopedLearningHref("/mock-exam", activeScope);
  const trainingEvidenceStatus = options.trainingEvidenceStatus ?? "ready";
  const needsDiagnosisEntry = trainingEvidenceStatus === "ready" && scopedTrainingRecords.length === 0;
  const remediationSummary = buildWorkspaceRemediationSummary(
    examCode,
    trainingHref,
    diagnosisHref,
    latestRecord
  );
  const mockPendingBase = `\u9700\u8981\u81F3\u5C11 ${MIN_DIAGNOSIS_TRAINING_SESSIONS} \u7EC4\u4E13\u9879\u548C ${MIN_DIAGNOSIS_ANSWERED_QUESTIONS} \u9898\u6709\u6548\u4F5C\u7B54\u3002`;
  const mockBlockedByStructure = profile.mockCredibility === "unconfigured";
  const mockBlockedByCoverage = hasMinimumEvidence && mockContentReadiness.status !== "ready" && mockContentReadiness.missingSectionLabels.length > 0;
  const mockUnavailableReason = !hasMinimumEvidence ? `${profile.copy.mockPendingDescription} ${mockPendingBase}` : mockBlockedByStructure ? "\u8003\u8BD5\u7ED3\u6784\u5C1A\u672A\u58F0\u660E\u5168\u771F\u9898\u91CF\uFF0C\u6682\u4E0D\u80FD\u5F00\u653E\u6A21\u8003\u5165\u53E3\u3002" : mockBlockedByCoverage ? `\u6A21\u8003\u9700\u8981\u5168\u79D1\u5185\u5BB9\u5C31\u7EEA\uFF1B\u4ECD\u7F3A\uFF1A${mockContentReadiness.missingSectionLabels.join("\u3001")}\u3002` : `${profile.copy.mockPendingDescription} ${mockPendingBase}`;
  const mockExamSummary = {
    status: mockExamAvailable ? "available" : "unavailable",
    title: mockExamAvailable ? "\u8BAD\u7EC3\u8BC1\u636E\u5DF2\u6EE1\u8DB3\u6A21\u8003\u5165\u53E3" : "\u5148\u8865\u9F50\u8BAD\u7EC3\u8BC1\u636E\u518D\u8FDB\u5165\u6A21\u8003",
    description: mockExamAvailable ? profile.copy.mockReadyDescription : mockUnavailableReason,
    primaryAction: {
      id: mockExamAvailable ? "start-real-mock-exam" : "continue-training-before-mock",
      label: mockExamAvailable ? "\u8FDB\u5165\u6A21\u62DF\u8003\u8BD5" : "\u7EE7\u7EED\u4E13\u9879\u8BAD\u7EC3",
      kind: mockExamAvailable ? "primary" : "secondary",
      href: mockExamAvailable ? mockExamHref : trainingHref
    },
    progress: {
      sessionCount: evidence.recordCount,
      sessionRequired: MIN_DIAGNOSIS_TRAINING_SESSIONS,
      answeredCount: evidence.answeredCount,
      answeredRequired: MIN_DIAGNOSIS_ANSWERED_QUESTIONS
    },
    credibility: profile.mockCredibility
  };
  const nextStep = buildWorkspaceNextStep({
    examCode,
    contentReadiness,
    prescription,
    remediationSummary,
    mockExamSummary,
    diagnosisHref,
    needsDiagnosisEntry,
    emptyDiagnosisDescription: profile.copy.emptyDiagnosisDescription,
    digestRecentMock: isInMockDigestionWindow(latestSnapshot)
  });
  const contentPack = buildContentPackModel(examCode);
  const evidenceAbilityLine = buildEvidenceAbilityLine({
    evidence,
    hasMinimumEvidence,
    weakestAccuracy: weakestModule?.averageAccuracy ?? 0,
    latestSnapshot
  });
  const chrome = buildWorkspaceChrome({
    nextStep,
    examCode,
    prescription,
    remediationSummary,
    evidenceAbilityLine
  });
  return {
    viewer,
    contentPack,
    contentReadiness,
    currentPath: buildTrainingLoop(resolveWorkspaceLoopStep(nextStep)),
    pageTitle: `${viewer.activeExamSpace.examName} \u5DE5\u4F5C\u53F0`,
    pageSubtitle: `${formatExamSpaceIdentityLabel(examCode)} \xB7 ${profile.copy.structureCue}`,
    nextStep,
    profile,
    chrome,
    skillRails: buildWorkspaceSkillRails(examCode, contentReadiness.sectionReadiness, activeScope),
    todayFocus: prescription,
    writingFocus,
    abilitySummary: {
      ...chrome.abilityLine,
      updatedAtLabel: latestRecord ? `\u6700\u8FD1\u8BAD\u7EC3\uFF1A${formatTrainingRecordTime(latestRecord.completedAt)}` : "\u6682\u65E0\u8BAD\u7EC3\u8BB0\u5F55"
    },
    mockExamSummary,
    remediationSummary,
    metrics: [
      {
        label: "\u8BCA\u65AD\u72B6\u6001",
        value: evidence.recordCount === 0 ? "\u672A\u5EFA\u7ACB" : hasMinimumEvidence ? "\u53EF\u66F4\u65B0" : "\u6837\u672C\u4E0D\u8DB3",
        tone: hasMinimumEvidence ? "success" : evidence.recordCount === 0 ? "neutral" : "warning",
        caption: evidence.evidenceLabel
      },
      {
        label: "\u8BAD\u7EC3\u8BB0\u5F55",
        value: `${evidence.recordCount} \u7EC4`,
        tone: evidence.recordCount >= MIN_DIAGNOSIS_TRAINING_SESSIONS ? "success" : "warning",
        caption: `\u8981\u6C42 ${MIN_DIAGNOSIS_TRAINING_SESSIONS} \u7EC4`
      },
      {
        label: "\u6709\u6548\u4F5C\u7B54",
        value: `${evidence.answeredCount} \u9898`,
        tone: evidence.answeredCount >= MIN_DIAGNOSIS_ANSWERED_QUESTIONS ? "success" : "warning",
        caption: `\u8981\u6C42 ${MIN_DIAGNOSIS_ANSWERED_QUESTIONS} \u9898`
      },
      {
        label: "\u6A21\u8003\u72B6\u6001",
        value: mockExamAvailable ? "\u53EF\u5F00\u59CB" : "\u7B49\u5F85\u6570\u636E",
        tone: mockExamAvailable ? "current" : "locked",
        caption: mockExamAvailable ? "\u8D70\u771F\u5B9E\u8003\u8BD5\u5165\u53E3" : "\u5148\u5B8C\u6210\u4E13\u9879\u8BAD\u7EC3"
      }
    ]
  };
}
function buildTcfWorkspacePageModel(viewer, trainingRecords = [], readinessInput = {}, options = {}) {
  return buildExamWorkspacePageModel(viewer, trainingRecords, readinessInput, options);
}
export {
  DIAGNOSIS_ENTRY_REDIRECT,
  DIAGNOSIS_INTENSITY,
  DIAGNOSIS_INTENSITY_LABEL,
  DIAGNOSIS_MODULE_ID2 as DIAGNOSIS_MODULE_ID,
  DIAGNOSIS_MODULE_LABEL,
  DIAGNOSIS_QUESTIONS_PER_SKILL,
  OPEN_RESPONSE_SESSION_PRESET,
  TRAINING_INTENSITY_PRESETS,
  WRITING_EVIDENCE_STALE_MS,
  WRITING_LOW_SCORE_RATIO,
  WRITING_STRONG_SCORE_RATIO,
  analyzeLearningWeakAreas,
  attemptSpecToTrainingSession,
  attemptWorkspaceHref,
  buildAbilityDiagnosisPageModel,
  buildAbilityWritingEvidenceBand,
  buildAttemptHref,
  buildDiagnosisEntryAction,
  buildDiagnosisSessionHref,
  buildExamPublicLandingModel,
  buildExamSelectionPageModel,
  buildExamTrainingEntryPageModel,
  buildExamWorkspacePageModel,
  buildExamWorkspaceProfile,
  buildExecutableTrainingPrescription,
  buildFullMockModules,
  buildLearnerAccessibleBankIdSet,
  buildLearnerContentAccessGate,
  buildLearnerContentReadiness,
  buildLearningAdvisorResult,
  buildLearningRecommendationsFromWeakAreas,
  buildLearningRouteScope,
  buildMockExamEvidenceRecordsFromAbilitySnapshots,
  buildMockExamPageModel,
  buildScopedLearningHref,
  buildTcfTrainingEntryPageModel,
  buildTcfWorkspacePageModel,
  buildTrainingEntryHref,
  buildTrainingLoop,
  buildTrainingSessionHref,
  buildUserLearningContext,
  buildWritingFocusPrescription,
  buildWritingTrainingEntryHref,
  canAccessExam,
  canAccessLanguage,
  canEnterLearningRoute,
  canStartFullMockExam,
  chooseDefaultActiveExam,
  countClosedChoiceWrongMetrics,
  examDeclaresOfficialMockCounts,
  examHasWritingSkill,
  formatExamSpaceIdentityLabel,
  formatExamSpaceLanguageCue,
  getEntitlementExpiry,
  getExamSelectionCatalog,
  getLearningModuleLabel,
  hasActiveEntitlement,
  hasEntryAbilityDiagnosis,
  isClosedChoiceWrongMetric,
  isGrantActive,
  isLearnerAccessibleLiveBank,
  isMockAttempt,
  isOpenResponseRuntimeReadySkillId,
  isOpenResponseSkillId,
  isPracticeAttempt,
  listPublicMarketingPaths,
  mapAiLearningSuggestions,
  normalizeAttemptSpec,
  normalizeExamType,
  normalizeLanguageId,
  normalizeOptionalLevel,
  normalizeOptionalSessionTaskNumber,
  normalizeOptionalSessionTopicId,
  parseAttemptSpec,
  parseTrainingLaunchIntent,
  parseTrainingSessionSpec,
  resolveAbilityDiagnosisPageStatus,
  resolveDiagnosisPack,
  resolveExamEntitlementStatus,
  resolveExamSpaceTheme,
  resolveLearnerTrainingSectionId,
  resolveMockExamPageStatus,
  resolveOpenResponseRuntimeBlockReason,
  resolveQuizLaunchGate,
  resolveQuizLaunchScope,
  resolveReadyDiagnosisSectionIds,
  resolveRecommendedWritingTaskNumber,
  resolveSessionSizeForSkill,
  resolveTrainingIntensityPreset,
  resolveTrainingSessionConfig,
  sampleDiagnosisPackQuestions,
  selectLearnerTrainingBankCandidates,
  summarizeWritingEvidence
};
